import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import {
  AlertCircle,
  Bell,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  ExternalLink,
  FileText,
  GitBranch,
  ListFilter,
  Loader2,
  Paperclip,
  Plus,
  Printer,
  Settings,
  X,
} from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import QBDealPicker, { type QBDealOption } from '../components/sales-activity/QBDealPicker';
import { db, storage } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections } from '../utils/CRMContext';
import { useBillingPayablesAccess } from '../hooks/useBillingPayablesAccess';
import { canReviewDesignerInvoices } from '../utils/projectPLAccess';
import { subscribePendingReviewInvoices } from '../utils/designerInvoiceService';
import BillingsHubModal from '../components/finances/BillingsHubModal';
import BillPayFlowPanel from '../components/finances/BillPayFlowPanel';
import {
  projectHubViewModeForHotkey,
  projectHubViewPath,
} from '../hooks/useProjectHubViewModeHotkeys';
import { isEditableTarget } from '../utils/isEditableTarget';
import {
  canAccessProjectHubChangeOrdersTab,
  canAccessProjectHubSuppliersTab,
} from '../utils/userRoles';
import { usePaymentLagSettings } from '../hooks/usePaymentLagSettings';
import { getProjectDisplayName } from '../utils/projectName';
import { mirrorAllOnboardingTasks } from '../utils/onboardingTaskBridge';
import { taskEffectiveEndDate } from '../utils/paymentDueFlags';
import { firestoreSnapshotListenError } from '../utils/firestoreListenDebug';
import {
  BILLING_READY_STATUS_LABELS,
  buildReadyToBillQueue,
  compareReadyToBillBillingChronology,
  invoiceFinanceApproved,
  invoiceWorkflowStage,
  shouldHideReadyToBillRow,
  type BillingReadyItem,
  previousBillingReadyStatus,
  type BillingReadyStatus,
  type ReadyToBillRow,
} from '../utils/readyToBill';
import {
  exportBillingPayablesPdf,
  installmentSliceMatchesFilter,
  isPaymentStateFilter,
  type BillingPayablesLedger,
} from '../utils/billingPayablesReport';
import {
  buildProjectBillingSummaries,
  isBillingHistoryLine,
  isDealInDefaultBillingProjectScope,
  isOverheadBillingDealId,
  payableStatusForBill,
  type ProjectBillingSummary,
  type ProjectMoneyLine,
} from '../utils/projectBillingSummary';
import {
  subscribeJobPurchaseRequisitions,
  subscribeOverheadPurchaseRequisitions,
} from '../utils/purchasingRequisitions';
import { recordAchPayableForIssuedPurchase } from '../utils/purchaseOrderIssueActions';
import { purchaseRequisitionPath } from '../utils/purchasingNotifications';
import {
  deleteBillingReadyItem,
  markReadyToBill,
  markSalesOrderInstallmentCollected,
  subscribeBillingReadyItems,
  updateBillingReadyItemStatus,
} from '../utils/readyToBillService';
import { buildPaymentScheduleBreakdown, invoicedInstallmentLocks, shouldAutoCreateProgressInvoice } from '../utils/paymentSchedule';
import { triggerQuickBooksInvoiceForSalesOrder } from '../utils/quickbooksInvoiceTrigger';
import PartialInvoicePackageModal, {
  type PartialInvoiceSelection,
} from '../components/portal/PartialInvoicePackageModal';
import { loadBillablePackagesForDeal } from '../utils/partialInvoicePackages';
import { allocateMilestoneSlice, milestonePackageChoices } from '../utils/partialInvoiceAllocation';
import {
  describeSupplierInstallmentTrigger,
  expandSupplierBillPayments,
  installmentApprovalRequired,
  nextUnpaidInstallmentIndex,
  parsePaidAmountInput,
  recordedSupplierBillCashPaid,
  paidInstallmentIds,
  releasedInstallmentIds,
  resolveInstallmentAmount,
  roundMoney,
  suggestEarlyPayAmount,
  supplierBillPaidAmount,
  type EarlyPayAmountSuggestion,
  SUPPLIER_BILL_CATEGORY_LABELS,
  previousPayableStatus,
  SUPPLIER_BILL_STATUS_FLOW,
  SUPPLIER_BILL_STATUS_LABELS,
  type SupplierBill,
  type SupplierBillCategory,
  type SupplierBillPaymentSlice,
  type SupplierBillStatus,
} from '../utils/supplierBills';
import {
  createSupplierBill,
  findSupplierBillForRfqAward,
  setSupplierBillInstallmentApproval,
  setSupplierBillInstallmentPayment,
  updateSupplierBillInstallmentPaidDate,
  setSupplierBillPaymentTerms,
  setSupplierBillQbReference,
  subscribeSupplierBills,
  updateSupplierBillDetails,
  updateSupplierBillStatus,
  uploadSupplierBillFile,
  findSupplierBillForCncOrgAward,
} from '../utils/supplierBillsService';
import { cncRfqAwardSubmissionKey, recordSupplierBillForRfqAward } from '../utils/supplierBillIngestion';
import SupplierPaymentTermsModal from '../components/vendor/SupplierPaymentTermsModal';
import { HoverTip, ScheduleTipContent } from '../components/billing/SupplierBillHover';
import PurchaseQbBillIndicator from '../components/purchasing/PurchaseQbBillIndicator';
import type { ChangeOrder, ProjectPLBucket, QBInvoice, RfqSubmission, SalesOrder } from '../types';
import type { PurchaseRequisition } from '../types/purchasing';
import { PROJECT_PL_BUCKETS } from '../types';
import { PROJECT_PL_BUCKET_LABELS } from '../utils/projectPL';
import '../components/pm/pm-shared.css';
import './FinancesHub.css';
import './BillingPayablesPage.css';

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCents = fmt;

const fmtDate = (d: Date | null | undefined) =>
  d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function approvalPaymentLabel(bill: SupplierBill, installmentIndex: number): string {
  const installments = bill.paymentTerms?.installments ?? [];
  const inst = installments[installmentIndex];
  const amount = inst ? resolveInstallmentAmount(inst, bill.amount) : bill.amount;
  const which = installments.length > 1
    ? `payment ${installmentIndex + 1} of ${installments.length}`
    : 'this payment';
  return `${bill.vendorName} — ${which} (${fmtCents(amount)})`;
}

/** Local-timezone YYYY-MM-DD for <input type="date"> (toISOString would shift days). */
const toDateInputValue = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function moneyLineStatusClass(kind: ProjectMoneyLine['statusKind']): string {
  switch (kind) {
    case 'paid':
      return 'bp-badge--paid';
    case 'invoiced':
      return 'bp-badge--entered_in_qb';
    case 'billable':
      return 'bp-badge--due';
    case 'quoted':
      return 'bp-badge--quoted';
    case 'pending':
      return 'bp-badge--pending';
    case 'ready':
      return 'bp-badge--ready_to_pay';
    default:
      return '';
  }
}

function renderMoneyLineStatus(line: ProjectMoneyLine): React.ReactNode {
  const cls = moneyLineStatusClass(line.statusKind);
  if (line.statusKind === 'paid') {
    return (
      <span className={`fin-pill ${cls}`}>
        <CheckCircle2 size={11} /> Paid
      </span>
    );
  }
  if (line.statusKind === 'billable') {
    return (
      <span className={`fin-pill ${cls}`}>
        <Clock size={11} /> Billable
      </span>
    );
  }
  if (line.statusKind === 'invoiced') {
    const short =
      line.statusLabel.startsWith('Invoiced #')
        ? line.statusLabel.replace('Invoiced in QB', 'Invoiced')
        : line.statusLabel;
    return (
      <span className={`fin-pill ${cls}`}>
        <FileText size={11} /> {short}
      </span>
    );
  }
  if (line.statusKind === 'quoted') {
    return <span className={`fin-pill ${cls}`}>Quoted</span>;
  }
  return <span className={`fin-pill ${cls}`.trim()}>{line.statusLabel}</span>;
}

function PoNumberChip({
  poNumber,
  purchaseRequisitionId,
}: {
  poNumber?: string;
  purchaseRequisitionId?: string;
}) {
  const value = poNumber?.trim();
  if (!value) return null;
  const prId = purchaseRequisitionId?.trim();
  if (!prId) return <span className="bp-po-chip">{value}</span>;
  return (
    <Link
      className="bp-po-chip bp-po-chip--link"
      to={purchaseRequisitionPath(prId)}
      title={`Open ${value} in Purchasing`}
      onClick={(event) => event.stopPropagation()}
    >
      {value}
    </Link>
  );
}

function ProjectLineItem({ line, hideDetail }: { line: ProjectMoneyLine; hideDetail?: boolean }) {
  const showDetail = !hideDetail && line.detail?.trim();
  return (
    <div className="bp-line-item">
      <span className="bp-line-item-name">{line.label}</span>
      {(showDetail || line.poNumber) && (
        <span className="bp-line-item-meta">
          {showDetail && <span className="bp-line-item-desc">{line.detail}</span>}
          <PoNumberChip poNumber={line.poNumber} purchaseRequisitionId={line.purchaseRequisitionId} />
        </span>
      )}
    </div>
  );
}

function partialSliceTitle(line: ProjectMoneyLine): string {
  if (/still to bill/i.test(line.detail)) return 'Remaining';
  const dash = line.detail.indexOf(' — ');
  if (dash > 0) return line.detail.slice(0, dash);
  const num = line.receivableRow?.quickbooksInvoiceNum;
  return num ? `Invoice #${num}` : line.label;
}

function partialSliceInvoiced(line: ProjectMoneyLine): boolean {
  const row = line.receivableRow;
  if (!row) return false;
  const tracked = row.tracked?.status;
  return !!(
    row.qbPaid
    || row.qbInvoiced
    || tracked === 'invoiced'
    || tracked === 'sent'
    || tracked === 'paid'
  );
}

function partialMilestoneDetail(slices: ProjectMoneyLine[]): string {
  const remainder = slices.find((slice) => /still to bill/i.test(slice.detail));
  if (remainder) return remainder.detail.replace(/\s*—\s*still to bill\s*$/i, '');
  const withGroup = slices.find((slice) => slice.detail.includes(' — '));
  if (withGroup) return withGroup.detail.slice(withGroup.detail.indexOf(' — ') + 3);
  return slices[0]?.detail || '';
}

function partialMilestoneAmount(slices: ProjectMoneyLine[]): number {
  const scheduled = slices
    .map((slice) => slice.receivableRow?.scheduleAmount)
    .find((amount) => typeof amount === 'number' && amount > 0);
  if (scheduled) return scheduled;
  return slices.reduce((sum, slice) => sum + slice.amount, 0);
}

function partialInvoiceStages(slices: ProjectMoneyLine[]): BreadcrumbStage[] {
  return slices.map((slice) => {
    const stage = slice.receivableRow ? (invoiceWorkflowStage(slice.receivableRow) ?? 0) : 0;
    return {
      label: `${partialSliceTitle(slice)} — ${INVOICE_STAGE_LABELS[stage] || 'Forecast'}`,
      state: stage >= 4 ? 'done' : stage === 0 ? 'todo' : 'current',
      payment: true,
    };
  });
}

function sliceNeedsInvoiceGate(line: ProjectMoneyLine): boolean {
  const row = line.receivableRow;
  if (!row) return false;
  if (invoiceWorkflowStage(row) === 2) return true;
  if (row.qbInvoiced && !row.qbPaid && !invoiceFinanceApproved(row)) return true;
  return !row.tracked && !row.qbInvoiced && !row.qbPaid;
}

function PartialInvoiceParentItem({
  line,
  slices,
  expanded,
  onToggle,
}: {
  line: ProjectMoneyLine;
  slices: ProjectMoneyLine[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const invoicedCount = slices.filter(partialSliceInvoiced).length;
  return (
    <div className="bp-line-item">
      <span className="bp-line-item-name">{line.label}</span>
      <span className="bp-line-item-meta">
        {line.detail?.trim() && <span className="bp-line-item-desc">{line.detail}</span>}
        <button
          type="button"
          className={`bp-terms-chip bp-terms-chip--toggle${expanded ? ' bp-terms-chip--open' : ''}`}
          aria-expanded={expanded}
          onClick={onToggle}
        >
          {invoicedCount} of {slices.length} invoiced
          <ChevronDown size={11} className="bp-terms-chip-chevron" />
        </button>
      </span>
    </div>
  );
}

type InvoiceCluster = { line: ProjectMoneyLine; slices: ProjectMoneyLine[] | null };

function clusterPartialInvoiceLines(lines: ProjectMoneyLine[]): InvoiceCluster[] {
  const buckets = new Map<string, ProjectMoneyLine[]>();
  const order: string[] = [];
  for (const line of lines) {
    const row = line.receivableRow;
    const family = row?.kind === 'installment' && row.installmentId
      ? `${row.dealId}:${row.installmentId}`
      : line.key;
    if (!buckets.has(family)) {
      buckets.set(family, []);
      order.push(family);
    }
    buckets.get(family)!.push(line);
  }
  return order.map((key) => {
    const slices = buckets.get(key) || [];
    if (slices.length < 2) return { line: slices[0], slices: null };
    const nextOpen = slices.find((slice) => !partialSliceInvoiced(slice)) || slices[slices.length - 1];
    return {
      slices,
      line: {
        ...nextOpen,
        key: `partial-group:${key}`,
        label: slices[0].receivableRow?.label || slices[0].label,
        detail: partialMilestoneDetail(slices),
        amount: partialMilestoneAmount(slices),
        dueDate: nextOpen.dueDate,
        estimated: nextOpen.estimated,
        statusLabel: nextOpen.statusLabel,
        statusKind: nextOpen.statusKind,
        receivableRow: undefined,
      },
    };
  });
}

function ProjectCardKpis({ summary }: { summary: ProjectBillingSummary }) {
  const { totals } = summary;
  const kpis: Array<{ key: string; label: string; value: string; accent?: boolean }> = [];

  if (totals.receivableOutstanding > 0) {
    kpis.push({ key: 'ar', label: 'AR due', value: fmt(totals.receivableOutstanding), accent: true });
  }
  if (totals.payableOpen > 0) {
    kpis.push({ key: 'ap', label: 'AP open', value: fmt(totals.payableOpen) });
  }
  if (totals.openQuotes > 0) {
    kpis.push({ key: 'quotes', label: 'Quotes', value: fmt(totals.openQuotes) });
  }

  if (kpis.length === 0) return null;

  return (
    <div className="bp-project-kpis">
      {kpis.map((kpi) => (
        <div key={kpi.key} className={`bp-kpi${kpi.accent ? ' bp-kpi--accent' : ''}`}>
          <span className="bp-kpi-value">{kpi.value}</span>
          <span className="bp-kpi-label">{kpi.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Payable lifecycle for the stage breadcrumb (void bills show no breadcrumb). */
/** "In QuickBooks" = CRM-created (or manually linked) QB bill ready for Juliana to pay. */
const PAYABLE_STAGE_LABELS = ['Pending review', 'Ready to pay', 'In QuickBooks', 'Paid'];

function payableStageIndex(status: SupplierBillStatus): number | null {
  switch (status) {
    case 'pending_review':
      return 0;
    case 'ready_to_pay':
      return 1;
    case 'entered_in_qb':
      return 2;
    case 'paid':
      return 3;
    default:
      return null;
  }
}

/** Invoice lifecycle for the stage breadcrumb (dismissed rows show no breadcrumb). */
const INVOICE_STAGE_LABELS = ['Forecast', 'Ready to bill', 'Invoiced in QB', 'Sent to client', 'Paid'];

type BreadcrumbStage = {
  label: string;
  state: 'done' | 'current' | 'todo';
  /** Payment dots render slightly differently from workflow dots. */
  payment?: boolean;
  /** Approved for payment but not yet paid — rendered with a ring. */
  approved?: boolean;
};

/** Simple sequential lifecycle → stages (everything before `current` is done). */
function stagesFromIndex(labels: string[], current: number): BreadcrumbStage[] {
  const complete = current === labels.length - 1;
  return labels.map((label, i) => ({
    label,
    state: i < current ? 'done' : i === current ? (complete ? 'done' : 'current') : 'todo',
  }));
}

/**
 * Bill lifecycle stages. Bills with a multi-installment schedule replace the
 * single "Paid" stage with one dot per payment: once the bill is entered in
 * QB, the current dot is the next unpaid installment.
 */
function payableStages(
  bill: SupplierBill,
  termSlices: SupplierBillPaymentSlice[],
): BreadcrumbStage[] | null {
  const displayStatus = bill.status;
  const statusIdx = payableStageIndex(displayStatus);
  if (statusIdx === null) return null;
  const terms = bill.paymentTerms;
  if (!terms || termSlices.length < 2) {
    return stagesFromIndex(PAYABLE_STAGE_LABELS, statusIdx);
  }

  const stages: BreadcrumbStage[] = PAYABLE_STAGE_LABELS.slice(0, 3).map((label, i) => ({
    label,
    state: statusIdx > i ? 'done' : statusIdx === i ? 'current' : 'todo',
  }));

  // Workflow is complete through "In QuickBooks" — payments carry the trail.
  const inPaymentPhase = statusIdx >= 2;
  if (statusIdx === 2) stages[2] = { ...stages[2]!, state: 'done' };
  const nextUnpaid = nextUnpaidInstallmentIndex(terms, bill.installmentPayments);

  termSlices.forEach((slice, pos) => {
    const idx = slice.installmentIndex!;
    const paid = !!bill.installmentPayments?.[idx];
    const approved = !!bill.installmentApprovals?.[idx];
    stages.push({
      label: `Payment ${pos + 1} of ${termSlices.length} — ${fmt(slice.amount)}`,
      state:
        paid || bill.status === 'paid'
          ? 'done'
          : inPaymentPhase && idx === nextUnpaid
            ? 'current'
            : 'todo',
      payment: true,
      approved: approved && !paid,
    });
  });
  return stages;
}

/** Mini breadcrumb timeline: where this row sits in its lifecycle. */
function StageBreadcrumb({
  stages,
  extraTip,
}: {
  stages: BreadcrumbStage[];
  /** Extra hover content under the stage list (e.g. the payment schedule). */
  extraTip?: React.ReactNode;
}) {
  const currentIdx = stages.findIndex((s) => s.state === 'current');
  const currentLabel =
    currentIdx >= 0 ? stages[currentIdx]!.label : stages[stages.length - 1]?.label ?? '';

  const tip = (
    <div className="bp-tip-stages">
      {stages.map((stage, i) => (
        <div key={`${i}-${stage.label}`} className={`bp-tip-stage bp-tip-stage--${stage.state}`}>
          <span
            className={[
              'bp-stage-dot',
              stage.payment ? 'bp-stage-dot--payment' : '',
              stage.state === 'done' ? 'bp-stage-dot--done' : '',
              stage.state === 'current' ? 'bp-stage-dot--current' : '',
              stage.approved && stage.state !== 'done' ? 'bp-stage-dot--approved' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          <span className="bp-tip-stage-label">{stage.label}</span>
          {stage.state === 'current' && <span className="bp-tip-stage-now">now</span>}
        </div>
      ))}
      {extraTip && <div className="bp-tip-divider" />}
      {extraTip}
    </div>
  );

  return (
    <HoverTip tip={tip} wide={!!extraTip} block>
      <span
        className="bp-stage-crumbs"
        role="img"
        aria-label={
          currentIdx >= 0
            ? `Stage ${currentIdx + 1} of ${stages.length}: ${currentLabel}`
            : `All ${stages.length} stages complete`
        }
      >
        {stages.map((stage, i) => (
          <React.Fragment key={`${i}-${stage.label}`}>
            {i > 0 && (
              <span
                className={[
                  'bp-stage-line',
                  stage.state !== 'todo' ? 'bp-stage-line--done' : '',
                  stage.payment && !stages[i - 1]?.payment ? 'bp-stage-line--split' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            )}
            <span
              className={[
                'bp-stage-dot',
                stage.payment ? 'bp-stage-dot--payment' : '',
                stage.state === 'done' ? 'bp-stage-dot--done' : '',
                stage.state === 'current' ? 'bp-stage-dot--current' : '',
                stage.approved && stage.state !== 'done' ? 'bp-stage-dot--approved' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </React.Fragment>
        ))}
      </span>
    </HoverTip>
  );
}

function nextBillAction(status: SupplierBillStatus): SupplierBillStatus | null {
  const flow = SUPPLIER_BILL_STATUS_FLOW[status] ?? [];
  return flow.find((s) => s !== 'void') ?? null;
}

function billAdvanceActionLabel(next: SupplierBillStatus): string {
  switch (next) {
    case 'ready_to_pay':
      return 'Ready to pay';
    case 'entered_in_qb':
      return 'Confirm in QB';
    case 'paid':
      return 'Mark paid';
    default:
      return SUPPLIER_BILL_STATUS_LABELS[next];
  }
}

/** Who is expected to take this bill's next step. */
function billAdvanceActionWho(next: SupplierBillStatus): string {
  switch (next) {
    case 'ready_to_pay':
      return 'Finance';
    case 'entered_in_qb':
      return 'CRM / Exec assistant';
    case 'paid':
      return 'Exec assistant';
    default:
      return 'Finance';
  }
}

function billAdvanceActionTip(next: SupplierBillStatus): string {
  switch (next) {
    case 'ready_to_pay':
      return 'Confirms the bill details are correct and moves it into the payment queue. CRM then creates a QuickBooks bill for the approved amount when bill writes are on.';
    case 'entered_in_qb':
      return 'CRM usually creates the QuickBooks bill for the approved amount. Use this only to confirm a link or enter a QB reference if auto-create did not run.';
    case 'paid':
      return 'Confirm the amount paid. An early-pay discount is suggested when this payment is inside the window.';
    default:
      return `Advances the bill to “${SUPPLIER_BILL_STATUS_LABELS[next]}”.`;
  }
}

// ── Status filter ────────────────────────────────────────────────────────
type BpStatusFilter =
  | 'all'
  | 'to_invoice'
  | 'invoiced'
  | 'awaiting_approval'
  | 'to_pay'
  | 'paid'
  | 'quotes';

const BP_STATUS_FILTERS: Array<{ id: BpStatusFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'to_invoice', label: 'To invoice' },
  { id: 'invoiced', label: 'Invoiced' },
  { id: 'awaiting_approval', label: 'Awaiting approval' },
  { id: 'to_pay', label: 'To pay' },
  { id: 'paid', label: 'Paid' },
  { id: 'quotes', label: 'Open quotes' },
];

/** Next unpaid installment that still needs admin/CFO sign-off, if any. */
function nextUnpaidInstallmentAwaitingApproval(bill: SupplierBill): number | null {
  const terms = bill.paymentTerms;
  if (bill.status === 'paid' || bill.status === 'void') return null;
  if (!terms || !installmentApprovalRequired(terms)) return null;
  const nextIdx = nextUnpaidInstallmentIndex(terms, bill.installmentPayments);
  if (nextIdx === null || bill.installmentApprovals?.[nextIdx]) return null;
  return nextIdx;
}

function billHasInstallmentAwaitingApproval(bill: SupplierBill): boolean {
  if (bill.status === 'paid' || bill.status === 'void') return false;
  const terms = bill.paymentTerms;
  if (terms && installmentApprovalRequired(terms)) {
    return terms.installments.some(
      (_, idx) => !bill.installmentPayments?.[idx] && !bill.installmentApprovals?.[idx],
    );
  }
  // Purchasing approval does not release the bill. Finance still marks it ready to pay.
  return bill.status === 'pending_review';
}

function billHasInstallmentReadyToPay(bill: SupplierBill): boolean {
  if (bill.status === 'paid' || bill.status === 'void') return false;
  const terms = bill.paymentTerms;
  if (terms && installmentApprovalRequired(terms)) {
    return terms.installments.some(
      (_, idx) => !bill.installmentPayments?.[idx] && !!bill.installmentApprovals?.[idx],
    );
  }
  // No per-installment approvals: only bills past review are actually payable.
  return bill.status !== 'pending_review';
}

function lineMatchesStatusFilter(line: ProjectMoneyLine, filter: BpStatusFilter): boolean {
  // Unawarded quotes stay on the Open quotes filter only.
  if (filter === 'all') return !(line.direction === 'quote' && line.statusKind === 'quoted');
  const bill = line.supplierBill;
  switch (filter) {
    case 'to_invoice':
      return (
        line.direction === 'in'
        && line.statusKind !== 'invoiced'
        && line.statusKind !== 'paid'
        && line.statusKind !== 'dismissed'
      );
    case 'invoiced':
      return line.direction === 'in' && line.statusKind === 'invoiced';
    case 'awaiting_approval':
      return !!bill && billHasInstallmentAwaitingApproval(bill);
    case 'to_pay':
      // Approved quotes still need their bill recorded — that is a payment task.
      if (line.direction === 'quote') return line.statusKind === 'ready';
      return !!bill && billHasInstallmentReadyToPay(bill);
    case 'paid':
      // Include partially paid bills — their paid installments show in the panel.
      return (
        line.statusKind === 'paid'
        || (!!bill
          && bill.status !== 'void'
          && Object.keys(bill.installmentPayments ?? {}).length > 0)
      );
    case 'quotes':
      return line.direction === 'quote' && line.statusKind === 'quoted';
    default:
      return true;
  }
}

/**
 * Action button with a caption naming who is expected to click it, plus a
 * hover card explaining exactly what it does.
 */
function LineActionButton({
  label,
  who,
  tip,
  icon,
  primary,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  /** Role expected to perform this action (shown under the button). */
  who: string;
  tip: string;
  icon?: React.ReactNode;
  primary?: boolean;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <span className="bp-action-stack">
      <span className="bp-action-who">{who}</span>
      <HoverTip tip={<span className="bp-tip-hint">{tip}</span>}>
        <button
          type="button"
          className={`bp-btn-compact${primary ? ' bp-btn-compact--primary' : ''}`}
          disabled={busy || disabled}
          onClick={onClick}
        >
          {busy ? (
            <Loader2 size={12} className="bp-spin" />
          ) : (
            <>
              {icon}
              {label}
            </>
          )}
        </button>
      </HoverTip>
    </span>
  );
}

function PayableLineItem({
  line,
  bill,
  scheduleTip,
  paidCount,
  installmentCount,
  singleTermsLabel,
  expanded,
  onToggleSchedule,
}: {
  line: ProjectMoneyLine;
  bill: SupplierBill;
  /** Structured payment schedule hover card (rich detail for the EA). */
  scheduleTip?: React.ReactNode;
  paidCount: number;
  installmentCount: number;
  /** Concise terms text shown instead of a dropdown when there is one payment. */
  singleTermsLabel?: string;
  expanded: boolean;
  onToggleSchedule: () => void;
}) {
  const desc = bill.description?.trim();
  const showDesc =
    !!desc &&
    desc.toLowerCase() !== bill.vendorName.trim().toLowerCase() &&
    !desc.toLowerCase().startsWith(`${bill.vendorName.trim().toLowerCase()} —`);

  return (
    <div className="bp-line-item">
      <span className="bp-line-item-name">{bill.vendorName?.trim() || 'Unknown vendor'}</span>
      <span className="bp-line-item-meta">
        <span className="bp-category-pill">{line.label}</span>
        <PoNumberChip
          poNumber={line.poNumber || bill.poNumber}
          purchaseRequisitionId={line.purchaseRequisitionId || bill.purchaseRequisitionId}
        />
        {showDesc && (
          <span className="bp-line-item-desc" title={desc}>
            {desc}
          </span>
        )}
        {bill.fileUrl && (
          <a
            className="bp-file-link"
            href={bill.fileUrl}
            target="_blank"
            rel="noreferrer"
            title={bill.fileName || 'Bill file'}
          >
            <Paperclip size={11} />
          </a>
        )}
        {bill.paymentTerms && installmentCount === 1 && (
          <HoverTip tip={scheduleTip} wide>
            <span className="bp-terms-chip bp-terms-chip--static">
              {singleTermsLabel || '1 payment'}
            </span>
          </HoverTip>
        )}
        {bill.paymentTerms && installmentCount > 1 && (
          <HoverTip tip={scheduleTip} wide>
            <button
              type="button"
              className={`bp-terms-chip bp-terms-chip--toggle${expanded ? ' bp-terms-chip--open' : ''}`}
              aria-expanded={expanded}
              onClick={onToggleSchedule}
            >
              {paidCount > 0
                ? `${paidCount} of ${installmentCount} paid`
                : `${installmentCount} payments`}
              <ChevronDown size={11} className="bp-terms-chip-chevron" />
            </button>
          </HoverTip>
        )}
      </span>
    </div>
  );
}

function renderSupplierBillStatus(bill: SupplierBill): React.ReactNode {
  // With per-installment approval, the bill-level pill follows the *next*
  // payment: no "Ready to be paid" while that installment awaits sign-off.
  const terms = bill.paymentTerms;
  const awaitingIdx = nextUnpaidInstallmentAwaitingApproval(bill);
  if (awaitingIdx !== null && terms) {
    return (
      <HoverTip
        tip={
          <span className="bp-tip-hint">
            Payment {awaitingIdx + 1} of {terms.installments.length} needs admin/CFO approval before
            it can be paid
          </span>
        }
      >
        <span className="fin-pill bp-badge--pending">Awaiting approval</span>
      </HoverTip>
    );
  }
  const payable = payableStatusForBill(bill);
  const badgeKind =
    payable.kind === 'ready'
      ? 'ready_to_pay'
      : payable.kind === 'pending'
        ? 'pending_review'
        : bill.status;
  let label = payable.label;
  if (bill.status === 'paid') {
    const paidCash = recordedSupplierBillCashPaid(bill);
    if (paidCash > 0 && roundMoney(paidCash) !== roundMoney(bill.amount)) {
      label = `Paid · ${fmtCents(paidCash)}`;
    }
  }
  return (
    <span className={`fin-pill bp-badge--${badgeKind}`}>
      {label}
    </span>
  );
}

/** Compact QB-link chip — lives with the action buttons so it doesn't shift the stage breadcrumb. */
function QbBillStatusChip({ bill }: { bill: SupplierBill }) {
  if (!bill.qbBillStatus || bill.qbBillStatus === 'skipped') return null;
  return (
    <HoverTip
      block
      tip={
        <span className="bp-tip-hint">
          {bill.qbBillMessage?.trim()
            || (bill.qbBillStatus === 'linked'
              ? 'This payable already has a QuickBooks bill linked.'
              : 'QuickBooks does not have a linked vendor bill yet.')}
        </span>
      }
    >
      <PurchaseQbBillIndicator
        compact
        status={bill.qbBillStatus}
        url={bill.qbBillUrl}
        message={bill.qbBillMessage}
        vendorName={bill.vendorName}
        amount={bill.amount}
      />
    </HoverTip>
  );
}

/** QB bill # / memo under the bill status — click to edit inline. */
function QbRefNote({
  bill,
  canManage,
  busy,
  onSave,
}: {
  bill: SupplierBill;
  canManage: boolean;
  busy: boolean;
  onSave: (bill: SupplierBill, note: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const note = bill.qbReferenceNote?.trim() ?? '';
  const showAdd = !note && (bill.status === 'entered_in_qb' || bill.status === 'paid');
  if (!canManage) {
    return note ? <span className="bp-qb-ref">{note}</span> : null;
  }
  if (!note && !showAdd && !editing) return null;

  if (editing) {
    const commit = () => {
      setEditing(false);
      if (value.trim() !== note) onSave(bill, value);
    };
    return (
      <input
        className="bp-qb-ref-input"
        autoFocus
        value={value}
        placeholder="QB bill # / memo"
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  return (
    <HoverTip
      block
      tip={<span className="bp-tip-hint">Click to edit the QuickBooks bill # / memo</span>}
    >
      <button
        type="button"
        className={`bp-qb-ref bp-qb-ref--editable${note ? '' : ' bp-qb-ref--empty'}`}
        disabled={busy}
        onClick={() => {
          setValue(note);
          setEditing(true);
        }}
      >
        {busy ? <Loader2 size={10} className="bp-spin" /> : note || '+ QB bill #'}
      </button>
    </HoverTip>
  );
}

/**
 * Tracked-invoice status: keeps the styled pill and opens a small menu on
 * click for manual corrections (replaces the raw select).
 */
function TrackedStatusPill({
  line,
  tracked,
  busy,
  onTrackedStatus,
  onStepBack,
}: {
  line: ProjectMoneyLine;
  tracked: BillingReadyItem;
  busy: boolean;
  onTrackedStatus: (item: BillingReadyItem, status: BillingReadyStatus) => void;
  onStepBack: (item: BillingReadyItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (hostRef.current && !hostRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span ref={hostRef} className="bp-status-pill-menu">
      <HoverTip tip={<span className="bp-tip-hint">Click to change the status</span>}>
        <button
          type="button"
          className="bp-status-pill-btn"
          disabled={busy}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {renderMoneyLineStatus(line)}
        </button>
      </HoverTip>
      {open && (
        <div className="pm-dropdown bp-status-pill-dropdown" role="menu">
          {previousBillingReadyStatus(tracked.status) && (
            <button
              type="button"
              role="menuitem"
              className="pm-dropdown-item"
              onClick={() => {
                setOpen(false);
                onStepBack(tracked);
              }}
            >
              Step back
              <span className="bp-bill-settings-hint">
                {previousBillingReadyStatus(tracked.status) === 'forecast'
                  ? 'Forecast'
                  : BILLING_READY_STATUS_LABELS[previousBillingReadyStatus(tracked.status) as BillingReadyStatus]}
              </span>
            </button>
          )}
          {(Object.keys(BILLING_READY_STATUS_LABELS) as BillingReadyStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={tracked.status === s}
              className={`pm-dropdown-item${tracked.status === s ? ' bp-filter-menu-item--active' : ''}`}
              onClick={() => {
                setOpen(false);
                if (s !== tracked.status) onTrackedStatus(tracked, s);
              }}
            >
              {BILLING_READY_STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function SupplierBillSettingsMenu({
  bill,
  disabled,
  onTermsEdit,
  onStepBack,
  onVoid,
}: {
  bill: SupplierBill;
  disabled?: boolean;
  onTermsEdit: () => void;
  onStepBack: (to: SupplierBillStatus) => void;
  onVoid: () => void;
}) {
  const [open, setOpen] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);

  const showTerms = bill.status !== 'void' && bill.status !== 'paid';
  const stepBackTo = previousPayableStatus(bill.status);
  const canVoid = (SUPPLIER_BILL_STATUS_FLOW[bill.status] ?? []).includes('void');

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !hostRef.current) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const rect = hostRef.current?.getBoundingClientRect();
      if (!rect) return;
      const right = Math.max(8, window.innerWidth - rect.right);
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 96 && rect.top > spaceBelow) {
        setMenuPos({ bottom: window.innerHeight - rect.top + 4, right });
      } else {
        setMenuPos({ top: rect.bottom + 4, right });
      }
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (hostRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, close]);

  if (!showTerms && !stepBackTo && !canVoid) return null;

  return (
    <div
      ref={hostRef}
      className={`bp-bill-settings${open ? ' bp-bill-settings--open' : ''}`}
    >
      <button
        type="button"
        className="bp-btn-icon"
        aria-label="Bill options"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <Settings size={14} aria-hidden />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          className="bp-bill-settings-menu pm-dropdown"
          role="menu"
          style={{
            top: menuPos.top,
            bottom: menuPos.bottom,
            left: 'auto',
            right: menuPos.right,
          }}
        >
          {showTerms && (
            <button
              type="button"
              className="pm-dropdown-item"
              role="menuitem"
              onClick={() => {
                close();
                onTermsEdit();
              }}
            >
              Payment terms
              {bill.paymentTerms && (
                <span className="bp-bill-settings-hint">
                  {bill.paymentTerms.installments.length} installment
                  {bill.paymentTerms.installments.length === 1 ? '' : 's'}
                </span>
              )}
            </button>
          )}
          {stepBackTo && (
            <button
              type="button"
              className="pm-dropdown-item"
              role="menuitem"
              onClick={() => {
                close();
                onStepBack(stepBackTo);
              }}
            >
              Step back
              <span className="bp-bill-settings-hint">
                {SUPPLIER_BILL_STATUS_LABELS[stepBackTo]}
              </span>
            </button>
          )}
          {canVoid && (
            <button
              type="button"
              className="pm-dropdown-item bp-bill-settings-danger"
              role="menuitem"
              onClick={() => {
                close();
                onVoid();
              }}
            >
              Void bill
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function ProjectLineRow(props: {
  line: ProjectMoneyLine;
  canMarkReady: boolean;
  canManage: boolean;
  canApprove: boolean;
  busyKey: string | null;
  statusFilter: BpStatusFilter;
  taskEndDateById?: Map<string, Date>;
  onMarkReady: (row: ReadyToBillRow) => void;
  onMarkSent: (row: ReadyToBillRow) => void;
  onTrackedStatus: (item: BillingReadyItem, status: BillingReadyStatus) => void;
  onStepBackInvoice: (item: BillingReadyItem) => void;
  onBillStatus: (bill: SupplierBill, to: SupplierBillStatus, note?: string) => void;
  onBillQbModal: (bill: SupplierBill) => void;
  onBillQbRef: (bill: SupplierBill, note: string) => void;
  onBillAmountEdit: (bill: SupplierBill) => void;
  onBillTermsEdit: (bill: SupplierBill) => void;
  onRecordBill: (line: ProjectMoneyLine) => void;
  onInstallmentPaid: (bill: SupplierBill, installmentIndex: number, paid: boolean) => void;
  onInstallmentApproval: (bill: SupplierBill, installmentIndex: number, approved: boolean) => void;
  onInstallmentPaidDate: (bill: SupplierBill, installmentIndex: number, paidAt: Date) => void;
  /** Arms the Quick Look preview (Space bar) while the amount is hovered. */
  onPreviewHover: (line: ProjectMoneyLine | null) => void;
  /** Whether a stored quote/bill PDF exists for this line. */
  lineHasPreview: (line: ProjectMoneyLine) => boolean;
  /** Partial invoices of one milestone, shown in a dropdown like a bill's payments. */
  partialSlices?: ProjectMoneyLine[];
}) {
  const {
    line,
    canMarkReady,
    canManage,
    canApprove,
    busyKey,
    statusFilter,
    taskEndDateById,
    onMarkReady,
    onMarkSent,
    onTrackedStatus,
    onStepBackInvoice,
    onBillStatus,
    onBillQbModal,
    onBillQbRef,
    onBillAmountEdit,
    onBillTermsEdit,
    onRecordBill,
    onInstallmentPaid,
    onInstallmentApproval,
    onInstallmentPaidDate,
    onPreviewHover,
    lineHasPreview,
    partialSlices,
  } = props;

  const row = line.receivableRow;
  const tracked = row?.tracked;
  const bill = line.supplierBill;
  const next = bill ? nextBillAction(bill.status) : null;
  const isInvoice = line.direction === 'in';

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [partialsOpen, setPartialsOpen] = useState(false);
  const [openPaymentIdx, setOpenPaymentIdx] = useState<number | null>(null);

  /** Installment slices resolved against the live schedule (empty without terms). */
  const termSlices = useMemo<SupplierBillPaymentSlice[]>(() => {
    if (!bill?.paymentTerms?.installments?.length) return [];
    return expandSupplierBillPayments(bill, { taskEndDateById }).filter(
      (s) => s.installmentIndex !== undefined,
    );
  }, [bill, taskEndDateById]);

  // Payment-state filters auto-expand the schedule so matching installments show.
  const paymentStateFilterActive = isPaymentStateFilter(statusFilter);
  useEffect(() => {
    setScheduleOpen(paymentStateFilterActive && termSlices.length > 1);
  }, [paymentStateFilterActive, statusFilter, termSlices.length]);

  /** Whether an installment slice matches the active payment-state filter. */
  const sliceMatchesFilter = (slice: SupplierBillPaymentSlice): boolean => {
    if (!paymentStateFilterActive || !bill) return true;
    return installmentSliceMatchesFilter(bill, slice, statusFilter);
  };

  const scheduleTip =
    bill?.paymentTerms && termSlices.length > 0 ? (
      <ScheduleTipContent bill={bill} slices={termSlices} />
    ) : undefined;
  const paidCount = termSlices.filter(
    (s) => bill?.installmentPayments?.[s.installmentIndex!],
  ).length;
  const singleTermsLabel =
    bill?.paymentTerms && termSlices.length === 1
      ? describeSupplierInstallmentTrigger(
          bill.paymentTerms.installments[termSlices[0]!.installmentIndex!]!,
        )
      : undefined;
  const billStages = bill ? payableStages(bill, termSlices) : null;
  const awaitingApprovalIdx = bill ? nextUnpaidInstallmentAwaitingApproval(bill) : null;
  const nextUnpaidIdx = bill?.paymentTerms
    ? nextUnpaidInstallmentIndex(bill.paymentTerms, bill.installmentPayments)
    : null;
  const showBillAdvance =
    !!bill
    && !!next
    && awaitingApprovalIdx === null
    && !(next === 'paid' && installmentApprovalRequired(bill.paymentTerms));
  // Multi-installment bills hide the row-level Mark paid button. When the next
  // installment is already approved, offer a button that opens the schedule.
  const showOpenPayments =
    !isInvoice
    && !!bill
    && !scheduleOpen
    && termSlices.length > 1
    && next === 'paid'
    && awaitingApprovalIdx === null
    && nextUnpaidIdx !== null;

  useEffect(() => {
    if (openPaymentIdx == null || !bill?.id || !scheduleOpen) return;
    const el = document.getElementById(`bp-inst-${bill.id}-${openPaymentIdx}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const timer = window.setTimeout(() => setOpenPaymentIdx(null), 2400);
    return () => window.clearTimeout(timer);
  }, [openPaymentIdx, scheduleOpen, bill?.id]);

  // Payment-side rows with a source document support Quick Look (Space bar).
  const hasPreview = lineHasPreview(line);

  return (
    <>
    <div
      id={
        row?.tracked?.id
          ? `bp-ready-${row.tracked.id}`
          : bill?.id
            ? `bp-bill-${bill.id}`
            : undefined
      }
      className={[
        'bp-line-row',
        line.direction === 'quote' ? 'bp-line-row--quote' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isInvoice && partialSlices && partialSlices.length > 1 ? (
        <PartialInvoiceParentItem
          line={line}
          slices={partialSlices}
          expanded={partialsOpen}
          onToggle={() => setPartialsOpen((open) => !open)}
        />
      ) : isInvoice ? (
        <ProjectLineItem line={line} />
      ) : bill ? (
        <PayableLineItem
          line={line}
          bill={bill}
          scheduleTip={scheduleTip}
          paidCount={paidCount}
          installmentCount={termSlices.length}
          singleTermsLabel={singleTermsLabel}
          expanded={scheduleOpen}
          onToggleSchedule={() => setScheduleOpen((v) => !v)}
        />
      ) : (
        <ProjectLineItem line={line} />
      )}

      <span
        className={`bp-col-amt bp-line-amt${hasPreview ? ' bp-line-amt--peekable' : ''}`}
        onMouseEnter={hasPreview ? () => onPreviewHover(line) : undefined}
        onMouseLeave={hasPreview ? () => onPreviewHover(null) : undefined}
      >
        {hasPreview ? (
          <HoverTip
            tip={
              <span className="bp-tip-hint">
                Press <kbd>Space</kbd> to preview the quote PDF
              </span>
            }
          >
            {fmt(line.amount)}
          </HoverTip>
        ) : (
          fmt(line.amount)
        )}
        {canManage && bill && bill.amount === 0 && bill.status !== 'void' && bill.status !== 'paid' && (
          <button type="button" className="bp-amount-edit-btn" onClick={() => onBillAmountEdit(bill)}>
            Set
          </button>
        )}
      </span>

      <span className="bp-col-due bp-line-due">
        {line.dueDate ? (
          <>
            {fmtDate(line.dueDate)}
            {line.estimated && (
              <HoverTip
                tip={
                  <span className="bp-tip-hint">
                    {isInvoice
                      ? 'Estimated from the project schedule — firms up when marked ready for billing'
                      : 'Estimated from payment terms and the live schedule'}
                  </span>
                }
              >
                <span className="bp-est-mark" aria-label="Estimated date">
                  *
                </span>
              </HoverTip>
            )}
          </>
        ) : (
          '—'
        )}
      </span>

      <span className="bp-col-status">
        {isInvoice && tracked && canManage ? (
          <TrackedStatusPill
            line={line}
            tracked={tracked}
            busy={busyKey === tracked.id}
            onTrackedStatus={onTrackedStatus}
            onStepBack={onStepBackInvoice}
          />
        ) : bill ? (
          renderSupplierBillStatus(bill)
        ) : (
          renderMoneyLineStatus(line)
        )}
        {bill && (
          <QbRefNote
            bill={bill}
            canManage={canManage}
            busy={busyKey === `${bill.id}:qbref`}
            onSave={onBillQbRef}
          />
        )}
        {bill && billStages && <StageBreadcrumb stages={billStages} extraTip={scheduleTip} />}
        {partialSlices && partialSlices.length > 1 && (
          <StageBreadcrumb stages={partialInvoiceStages(partialSlices)} />
        )}
        {isInvoice && row && invoiceWorkflowStage(row) !== null && (
          <StageBreadcrumb stages={stagesFromIndex(INVOICE_STAGE_LABELS, invoiceWorkflowStage(row)!)} />
        )}
        {!canManage && bill && <QbBillStatusChip bill={bill} />}
      </span>

      {canManage && (
        <span className="bp-col-action bp-line-action">
          {bill && <QbBillStatusChip bill={bill} />}
          <div className="bp-line-action-buttons">
            {isInvoice && row && invoiceWorkflowStage(row) === 2 && (
              <LineActionButton
                label="Sent to client"
                who="Exec assistant"
                tip="Records that this invoice was emailed to the client — payment is the only step left."
                primary
                busy={busyKey === row.key}
                onClick={() => onMarkSent(row)}
              />
            )}
            {isInvoice && row && row.qbInvoiced && !row.qbPaid && !invoiceFinanceApproved(row) && canMarkReady && (
              <LineActionButton
                label="Approve"
                who="PM / Finance"
                tip="The invoice is already in QuickBooks. PM or Finance still has to approve it before it can be sent to the client."
                icon={<Bell size={12} />}
                primary
                busy={busyKey === row.key}
                onClick={() => onMarkReady(row)}
              />
            )}
            {isInvoice && row && !tracked && !row.qbInvoiced && !row.qbPaid && canMarkReady && (
              <LineActionButton
                label="Ready to invoice"
                who="PM / Finance"
                tip="Flags this milestone as ready to bill — it moves to the invoicing queue so the invoice can be created in QuickBooks."
                icon={<Bell size={12} />}
                busy={busyKey === row.key}
                onClick={() => onMarkReady(row)}
              />
            )}
            {line.direction === 'quote' && line.statusKind === 'ready' && line.rfqSubmissionId && (
              <LineActionButton
                label="Record bill"
                who="Finance"
                tip="Creates the supplier bill for this approved quote so payment terms can be set and payments tracked."
                primary
                busy={busyKey === line.key}
                onClick={() => onRecordBill(line)}
              />
            )}
            {line.direction === 'out'
              && !bill
              && line.purchaseRequisitionId
              && line.statusKind === 'pending' && (
              <LineActionButton
                label="Create payable"
                who="Finance"
                tip="Creates the supplier bill for this issued OpEx PO so you can enter it in QuickBooks and mark it paid."
                primary
                busy={busyKey === line.key}
                onClick={() => onRecordBill(line)}
              />
            )}
            {!isInvoice && bill && awaitingApprovalIdx !== null && (
              <LineActionButton
                label="Approve"
                who="Admin / CFO"
                tip="Releases the next installment so the exec assistant can pay it in QuickBooks. You can add a message for them, such as a QuickBooks bill update."
                primary
                disabled={!canApprove}
                busy={busyKey === `${bill.id}:appr:${awaitingApprovalIdx}`}
                onClick={() => onInstallmentApproval(bill, awaitingApprovalIdx, true)}
              />
            )}
            {!isInvoice && showBillAdvance && (
              <LineActionButton
                label={billAdvanceActionLabel(next!)}
                who={billAdvanceActionWho(next!)}
                tip={billAdvanceActionTip(next!)}
                primary
                busy={busyKey === bill.id}
                onClick={() => {
                  if (next === 'entered_in_qb') onBillQbModal(bill);
                  else if (next === 'paid') onBillStatus(bill, 'paid');
                  else onBillStatus(bill, next!);
                }}
              />
            )}
            {partialSlices && partialSlices.length > 1 && !partialsOpen && partialSlices.some(sliceNeedsInvoiceGate) && (
              <LineActionButton
                label="Show invoices"
                who="PM / Finance"
                tip="Opens the partial invoices so each one can move to the next step."
                icon={<ChevronDown size={12} />}
                primary
                onClick={() => setPartialsOpen(true)}
              />
            )}
            {showOpenPayments && bill && nextUnpaidIdx !== null && (
              <LineActionButton
                label="Show payments"
                who="Exec assistant"
                tip="Opens the payment schedule so the approved installment can be marked paid."
                icon={<ChevronDown size={12} />}
                primary
                onClick={() => {
                  setScheduleOpen(true);
                  setOpenPaymentIdx(nextUnpaidIdx);
                }}
              />
            )}
            {!isInvoice && bill && (
              <SupplierBillSettingsMenu
                bill={bill}
                disabled={busyKey === bill.id}
                onTermsEdit={() => onBillTermsEdit(bill)}
                onStepBack={(to) => onBillStatus(bill, to)}
                onVoid={() => {
                  if (
                    window.confirm(
                      `Void the ${bill.vendorName} bill for ${fmt(bill.amount)}? This removes it from open payables.`,
                    )
                  ) {
                    onBillStatus(bill, 'void');
                  }
                }}
              />
            )}
          </div>
        </span>
      )}
    </div>

    {partialSlices && partialSlices.length > 1 && partialsOpen && (
      <div className="bp-installment-panel">
        {partialSlices.map((slice) => {
          const sliceRow = slice.receivableRow;
          const sliceTracked = sliceRow?.tracked;
          const stage = sliceRow ? invoiceWorkflowStage(sliceRow) : null;
          return (
            <div
              key={slice.key}
              id={sliceTracked?.id ? `bp-ready-${sliceTracked.id}` : undefined}
              className="bp-installment-row"
            >
              <span className="bp-line-item bp-installment-name">
                <span className="bp-line-item-name">{partialSliceTitle(slice)}</span>
                {sliceRow?.quickbooksInvoiceNum && (
                  <span className="bp-line-item-desc">Invoice #{sliceRow.quickbooksInvoiceNum}</span>
                )}
              </span>
              <span className="bp-col-amt bp-line-amt">{fmt(slice.amount)}</span>
              <span className="bp-col-due bp-line-due">
                {slice.dueDate ? (
                  <>
                    {fmtDate(slice.dueDate)}
                    {slice.estimated && (
                      <span className="bp-est-mark" aria-label="Estimated date">*</span>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </span>
              <span className="bp-col-status">
                {sliceTracked && canManage ? (
                  <TrackedStatusPill
                    line={slice}
                    tracked={sliceTracked}
                    busy={busyKey === sliceTracked.id}
                    onTrackedStatus={onTrackedStatus}
                    onStepBack={onStepBackInvoice}
                  />
                ) : (
                  renderMoneyLineStatus(slice)
                )}
                {stage !== null && (
                  <StageBreadcrumb stages={stagesFromIndex(INVOICE_STAGE_LABELS, stage)} />
                )}
              </span>
              {canManage && (
                <span className="bp-col-action bp-line-action">
                  <div className="bp-line-action-buttons">
                    {sliceRow && stage === 2 && (
                      <LineActionButton
                        label="Sent to client"
                        who="Exec assistant"
                        tip="Records that this invoice was emailed to the client — payment is the only step left."
                        primary
                        busy={busyKey === sliceRow.key}
                        onClick={() => onMarkSent(sliceRow)}
                      />
                    )}
                    {sliceRow && sliceRow.qbInvoiced && !sliceRow.qbPaid && !invoiceFinanceApproved(sliceRow) && canMarkReady && (
                      <LineActionButton
                        label="Approve"
                        who="PM / Finance"
                        tip="The invoice is already in QuickBooks. PM or Finance still has to approve it before it can be sent to the client."
                        icon={<Bell size={12} />}
                        primary
                        busy={busyKey === sliceRow.key}
                        onClick={() => onMarkReady(sliceRow)}
                      />
                    )}
                    {sliceRow && !sliceTracked && !sliceRow.qbInvoiced && !sliceRow.qbPaid && canMarkReady && (
                      <LineActionButton
                        label="Ready to invoice"
                        who="PM / Finance"
                        tip="Flags this milestone as ready to bill — it moves to the invoicing queue so the invoice can be created in QuickBooks."
                        icon={<Bell size={12} />}
                        busy={busyKey === sliceRow.key}
                        onClick={() => onMarkReady(sliceRow)}
                      />
                    )}
                  </div>
                </span>
              )}
            </div>
          );
        })}
      </div>
    )}

    {bill && scheduleOpen && termSlices.length > 1 && (
      <div className="bp-installment-panel">
        {termSlices.map((slice, pos) => {
          if (!sliceMatchesFilter(slice)) return null;
          const idx = slice.installmentIndex!;
          const inst = bill.paymentTerms!.installments[idx];
          const payment = bill.installmentPayments?.[idx];
          const approval = bill.installmentApprovals?.[idx];
          const needsApproval = installmentApprovalRequired(bill.paymentTerms) && !approval;
          const instBusyKey = `${bill.id}:inst:${idx}`;
          const apprBusyKey = `${bill.id}:appr:${idx}`;
          return (
            <div
              key={idx}
              id={bill.id ? `bp-inst-${bill.id}-${idx}` : undefined}
              className={[
                'bp-installment-row',
                payment ? 'bp-installment-row--paid' : '',
                openPaymentIdx === idx ? 'bp-installment-row--flash' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="bp-line-item bp-installment-name">
                <span className="bp-line-item-name">
                  Payment {pos + 1} of {termSlices.length}
                </span>
                <span className="bp-line-item-desc">
                  {inst ? describeSupplierInstallmentTrigger(inst) : slice.label}
                </span>
                {!payment && approval?.note ? (
                  <span className="bp-installment-note">{approval.note}</span>
                ) : null}
                {inst?.documents?.length ? (
                  <span className="bp-installment-docs">
                    {inst.documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {doc.fileName}
                      </a>
                    ))}
                  </span>
                ) : null}
              </span>
              <span className="bp-col-amt bp-line-amt">
                {fmt(slice.amount)}
                <span className="bp-installment-pct">{Math.round(inst?.percent ?? 0)}%</span>
              </span>
              <span className="bp-col-due bp-line-due">
                {slice.dueDate ? (
                  <>
                    {fmtDate(slice.dueDate)}
                    {inst?.trigger === 'task_complete' && (
                      <HoverTip
                        tip={
                          <span className="bp-tip-hint">
                            Anchored to a schedule task — moves with the live plan
                          </span>
                        }
                      >
                        <span className="bp-est-mark" aria-label="Tracks the schedule">
                          *
                        </span>
                      </HoverTip>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </span>
              <span className="bp-col-status">
                {payment ? (
                  <HoverTip
                    tip={
                      <span className="bp-tip-hint">
                        Marked paid {fmtDate(payment.paidAt)}
                        {payment.byUserName ? ` by ${payment.byUserName}` : ''}
                        {canManage ? ' — click to adjust the date' : ''}
                      </span>
                    }
                  >
                    <span
                      className={`fin-pill bp-badge--paid${canManage ? ' bp-paid-pill--editable' : ''}`}
                    >
                      <CheckCircle2 size={11} /> Paid {fmtDate(payment.paidAt)}
                      {payment.amountPaid != null
                        && roundMoney(payment.amountPaid) !== roundMoney(slice.amount)
                        ? ` · ${fmtCents(payment.amountPaid)}`
                        : ''}
                      {canManage && (
                        <input
                          type="date"
                          className="bp-paid-date-overlay"
                          value={toDateInputValue(payment.paidAt)}
                          disabled={busyKey === `${bill.id}:pdate:${idx}`}
                          aria-label="Adjust the paid date"
                          onClick={(e) =>
                            (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.()
                          }
                          onChange={(e) => {
                            if (!e.target.value) return;
                            onInstallmentPaidDate(bill, idx, new Date(`${e.target.value}T12:00:00`));
                          }}
                        />
                      )}
                    </span>
                  </HoverTip>
                ) : needsApproval ? (
                  <HoverTip
                    tip={
                      <span className="bp-tip-hint">
                        An admin or the CFO must approve this installment before it can be paid
                      </span>
                    }
                  >
                    <span className="fin-pill bp-badge--pending">Awaiting approval</span>
                  </HoverTip>
                ) : approval ? (
                  <HoverTip
                    tip={
                      <span className="bp-tip-hint">
                        Approved for payment {fmtDate(approval.approvedAt)}
                        {approval.byUserName ? ` by ${approval.byUserName}` : ''} — ready to be paid
                        in QB
                        {approval.note ? `. ${approval.note}` : ''}
                      </span>
                    }
                  >
                    <span className="fin-pill bp-badge--quoted">Process Payment</span>
                  </HoverTip>
                ) : (
                  <span className="fin-pill bp-badge--due">Unpaid</span>
                )}
              </span>
              {(canManage || canApprove) && (
                <span className="bp-col-action bp-line-action">
                  <div className="bp-line-action-buttons">
                    {canApprove && !payment && installmentApprovalRequired(bill.paymentTerms) && (
                      approval ? (
                        <button
                          type="button"
                          className="bp-btn-compact"
                          disabled={busyKey === apprBusyKey}
                          title="Withdraw approval for this installment"
                          onClick={() => onInstallmentApproval(bill, idx, false)}
                        >
                          {busyKey === apprBusyKey ? (
                            <Loader2 size={12} className="bp-spin" />
                          ) : (
                            'Unapprove'
                          )}
                        </button>
                      ) : (
                        <HoverTip
                          tip={
                            <span className="bp-tip-hint">
                              Admin / CFO: releases this installment so the exec assistant can pay
                              it in QuickBooks. You can add a message for them.
                            </span>
                          }
                        >
                          <button
                            type="button"
                            className="bp-btn-compact bp-btn-compact--primary"
                            disabled={busyKey === apprBusyKey}
                            onClick={() => onInstallmentApproval(bill, idx, true)}
                          >
                            {busyKey === apprBusyKey ? (
                              <Loader2 size={12} className="bp-spin" />
                            ) : (
                              'Approve'
                            )}
                          </button>
                        </HoverTip>
                      )
                    )}
                    {canManage && !needsApproval && (payment ? (
                      <button
                        type="button"
                        className="bp-btn-compact"
                        disabled={busyKey === instBusyKey}
                        title="Un-mark this installment as paid"
                        onClick={() => onInstallmentPaid(bill, idx, false)}
                      >
                        {busyKey === instBusyKey ? (
                          <Loader2 size={12} className="bp-spin" />
                        ) : (
                          'Undo'
                        )}
                      </button>
                    ) : (
                      <HoverTip
                        tip={
                          <span className="bp-tip-hint">
                            Exec assistant: confirm the amount paid, including an early-pay discount when it applies
                          </span>
                        }
                      >
                        <button
                          type="button"
                          className="bp-btn-compact bp-btn-compact--primary"
                          disabled={busyKey === instBusyKey}
                          onClick={() => onInstallmentPaid(bill, idx, true)}
                        >
                          {busyKey === instBusyKey ? (
                            <Loader2 size={12} className="bp-spin" />
                          ) : (
                            'Mark paid'
                          )}
                        </button>
                      </HoverTip>
                    ))}
                  </div>
                </span>
              )}
            </div>
          );
        })}
        {(bill.paymentTerms?.earlyPayDiscountPercent && bill.paymentTerms?.earlyPayWithinDays) ? (
          <div className="bp-installment-note">
            {bill.paymentTerms.earlyPayDiscountPercent}% early-pay discount when paid within{' '}
            {bill.paymentTerms.earlyPayWithinDays} days.
          </div>
        ) : null}
        {bill.paymentTerms?.notes?.trim() && (
          <div className="bp-installment-note">{bill.paymentTerms.notes.trim()}</div>
        )}
      </div>
    )}
    </>
  );
}

function ProjectLineGroup(props: {
  label: 'Invoices' | 'Payments' | 'History';
  variant: 'invoices' | 'payments' | 'history';
  lines: ProjectMoneyLine[];
  canMarkReady: boolean;
  canManage: boolean;
  canApprove: boolean;
  busyKey: string | null;
  statusFilter: BpStatusFilter;
  taskEndDateById?: Map<string, Date>;
  onMarkReady: (row: ReadyToBillRow) => void;
  onMarkSent: (row: ReadyToBillRow) => void;
  onTrackedStatus: (item: BillingReadyItem, status: BillingReadyStatus) => void;
  onStepBackInvoice: (item: BillingReadyItem) => void;
  onBillStatus: (bill: SupplierBill, to: SupplierBillStatus, note?: string) => void;
  onBillQbModal: (bill: SupplierBill) => void;
  onBillQbRef: (bill: SupplierBill, note: string) => void;
  onBillAmountEdit: (bill: SupplierBill) => void;
  onBillTermsEdit: (bill: SupplierBill) => void;
  onRecordBill: (line: ProjectMoneyLine) => void;
  onInstallmentPaid: (bill: SupplierBill, installmentIndex: number, paid: boolean) => void;
  onInstallmentApproval: (bill: SupplierBill, installmentIndex: number, approved: boolean) => void;
  onInstallmentPaidDate: (bill: SupplierBill, installmentIndex: number, paidAt: Date) => void;
  onPreviewHover: (line: ProjectMoneyLine | null) => void;
  lineHasPreview: (line: ProjectMoneyLine) => boolean;
}) {
  const { label, variant, lines, ...handlers } = props;
  if (lines.length === 0) return null;

  return (
    <div className={`bp-line-group bp-line-group--${variant}`}>
      <div className="bp-group-rail">
        <span className={`bp-group-label bp-group-label--${variant}`}>{label}</span>
      </div>
      <div className="bp-line-group-rows">
        {(label === 'Invoices' ? clusterPartialInvoiceLines(lines) : lines.map((line) => ({ line, slices: null }))).map((cluster) => (
          <ProjectLineRow
            key={cluster.line.key}
            line={cluster.line}
            partialSlices={cluster.slices || undefined}
            {...handlers}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectSummaryCard(props: {
  summary: ProjectBillingSummary;
  cardClassName?: string;
  canMarkReady: boolean;
  canManage: boolean;
  canApprove: boolean;
  busyKey: string | null;
  statusFilter: BpStatusFilter;
  taskEndDateById?: Map<string, Date>;
  onMarkReady: (row: ReadyToBillRow) => void;
  onMarkSent: (row: ReadyToBillRow) => void;
  onTrackedStatus: (item: BillingReadyItem, status: BillingReadyStatus) => void;
  onStepBackInvoice: (item: BillingReadyItem) => void;
  onBillStatus: (bill: SupplierBill, to: SupplierBillStatus, note?: string) => void;
  onBillQbModal: (bill: SupplierBill) => void;
  onBillQbRef: (bill: SupplierBill, note: string) => void;
  onBillAmountEdit: (bill: SupplierBill) => void;
  onBillTermsEdit: (bill: SupplierBill) => void;
  onRecordBill: (line: ProjectMoneyLine) => void;
  onInstallmentPaid: (bill: SupplierBill, installmentIndex: number, paid: boolean) => void;
  onInstallmentApproval: (bill: SupplierBill, installmentIndex: number, approved: boolean) => void;
  onInstallmentPaidDate: (bill: SupplierBill, installmentIndex: number, paidAt: Date) => void;
  onPreviewHover: (line: ProjectMoneyLine | null) => void;
  lineHasPreview: (line: ProjectMoneyLine) => boolean;
  /** Navigates to this project's Suppliers tab in the Project Hub (null when no hub project). */
  onOpenSuppliers: (() => void) | null;
  /** Hub project id — arms T/F/S/C/L while the card is hovered. */
  hubProjectId?: string | null;
  onHubHover?: (projectId: string | null) => void;
}) {
  const { summary, cardClassName, onOpenSuppliers, hubProjectId, onHubHover, ...handlers } = props;
  const isOverheadCard = isOverheadBillingDealId(summary.dealId);

  const sortReceivables = (lines: ProjectMoneyLine[]) =>
    lines
      .filter((l) => l.direction === 'in')
      .sort((a, b) =>
        a.receivableRow && b.receivableRow
          ? compareReadyToBillBillingChronology(a.receivableRow, b.receivableRow)
          : 0,
      );
  const currentLines = summary.lines.filter((l) => !isBillingHistoryLine(l));
  const historyLines = summary.lines.filter((l) => isBillingHistoryLine(l));
  const receivables = sortReceivables(currentLines);
  const payables = currentLines.filter((l) => l.direction === 'out');
  const quotes = currentLines.filter((l) => l.direction === 'quote');
  const paymentLines = [...quotes, ...payables];
  const hasLines = receivables.length > 0 || paymentLines.length > 0 || historyLines.length > 0;

  return (
    <article
      className={['bp-project-card', cardClassName].filter(Boolean).join(' ')}
      id={`bp-project-${summary.dealId}`}
      onMouseEnter={hubProjectId && onHubHover ? () => onHubHover(hubProjectId) : undefined}
      onMouseLeave={onHubHover ? () => onHubHover(null) : undefined}
    >
      <header className="bp-project-head">
        <h3 className="bp-project-title">{summary.dealLabel}</h3>
        {onOpenSuppliers && (
          <button
            type="button"
            className="bp-suppliers-link"
            onClick={onOpenSuppliers}
            title="Open this project's Suppliers tab in the Project Hub"
          >
            <Building2 size={12} /> Suppliers
          </button>
        )}
        <ProjectCardKpis summary={summary} />
      </header>

      {hasLines ? (
        <div className="bp-project-body">
          <div className={`bp-line-table${handlers.canManage ? '' : ' bp-line-table--no-action'}`}>
            <div className="bp-line-groups">
              <ProjectLineGroup label="Invoices" variant="invoices" lines={receivables} {...handlers} />
              {receivables.length > 0 && paymentLines.length > 0 && (
                <div className="bp-group-gap" aria-hidden="true" />
              )}
              <ProjectLineGroup label="Payments" variant="payments" lines={paymentLines} {...handlers} />
              {historyLines.length > 0 && (
                <>
                  <div className="bp-group-gap" aria-hidden="true" />
                  <ProjectLineGroup label="History" variant="history" lines={historyLines} {...handlers} />
                </>
              )}
            </div>
          </div>
        </div>
      ) : isOverheadCard ? (
        <p className="bp-overhead-empty">No OpEx payables match the current filter.</p>
      ) : null}
    </article>
  );
}

const BILL_CATEGORIES = Object.keys(SUPPLIER_BILL_CATEGORY_LABELS) as SupplierBillCategory[];

const BillingPayablesPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollDealId = searchParams.get('deal');
  const scrollBillId = searchParams.get('bill');
  const scrollItemId = searchParams.get('item');
  const {
    enhancedDeals,
    timberProjects,
    milestones,
    rfqSubmissions,
    rfqProjects,
    supplierOrgs,
    projectTasks,
    taskGroups,
    projectOnboardings,
    crmUsers,
  } = useCRM();
  useCRMCollections(['projectTasks', 'taskGroups', 'projectOnboardings', 'milestones']);
  const { lagSettings: paymentLagSettings } = usePaymentLagSettings();

  // Include onboarding-checklist mirror tasks — often the only members of the
  // Production group, which anchors the 40% installment.
  const scheduleTasks = useMemo(
    () => [...projectTasks, ...mirrorAllOnboardingTasks(projectOnboardings, taskGroups)],
    [projectTasks, projectOnboardings, taskGroups],
  );

  /** Live task end dates — resolves task-anchored supplier payment terms. */
  const taskEndDateById = useMemo(() => {
    const m = new Map<string, Date>();
    for (const t of scheduleTasks) {
      if (!t.id) continue;
      const end = taskEffectiveEndDate(t);
      if (end) m.set(t.id, end);
    }
    return m;
  }, [scheduleTasks]);
  const { canManage, canMarkReady, canApprove, canView, designations } = useBillingPayablesAccess();
  const canAccessSuppliersTab = canAccessProjectHubSuppliersTab(user?.role);
  const canAccessChangeOrdersTab = canAccessProjectHubChangeOrdersTab(user?.role, user?.designations);
  const meForReview = crmUsers.find((u) => u.uid === user?.id);
  const canReviewInvoices = canReviewDesignerInvoices({
    authRole: user?.role,
    designations: meForReview?.designations ?? designations,
    briefingProfile:
      user?.executiveBriefing?.profile ?? meForReview?.executiveBriefing?.profile ?? null,
  });
  const [pendingInvoiceReviewCount, setPendingInvoiceReviewCount] = useState(0);

  const [salesOrders, setSalesOrders] = useState<Array<SalesOrder & { id: string }>>([]);
  const [qbInvoices, setQbInvoices] = useState<Array<QBInvoice & { id: string }>>([]);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [trackedItems, setTrackedItems] = useState<BillingReadyItem[]>([]);
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [jobPurchaseOrders, setJobPurchaseOrders] = useState<PurchaseRequisition[]>([]);
  const [overheadPurchaseOrders, setOverheadPurchaseOrders] = useState<PurchaseRequisition[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showBillingsHub, setShowBillingsHub] = useState(false);
  const [showPayFlow, setShowPayFlow] = useState(false);

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [partialPicker, setPartialPicker] = useState<{
    row: ReadyToBillRow;
    structures: Awaited<ReturnType<typeof loadBillablePackagesForDeal>>['structures'];
    packages: ReturnType<typeof milestonePackageChoices>['packages'];
    baseAmount: number;
    milestoneAmount: number;
    coAdjustments: Array<{ reference: string; title: string; amount: number }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listenError, setListenError] = useState<string | null>(null);

  const [qbModalBill, setQbModalBill] = useState<SupplierBill | null>(null);
  const [qbNote, setQbNote] = useState('');
  const [payConfirm, setPayConfirm] = useState<{
    bill: SupplierBill;
    installmentIndex: number | null;
    suggestion: EarlyPayAmountSuggestion;
  } | null>(null);
  const [payAmountInput, setPayAmountInput] = useState('');
  const [approvePrompt, setApprovePrompt] = useState<{
    bill: SupplierBill;
    installmentIndex: number;
  } | null>(null);
  const [approveNote, setApproveNote] = useState('');

  // Quick Look (Space bar) preview of the quote/bill PDF under the cursor.
  const previewHoverLineRef = useRef<ProjectMoneyLine | null>(null);
  const [quickLook, setQuickLook] = useState<{ url: string; name: string } | null>(null);
  const [quickLookLoading, setQuickLookLoading] = useState(false);
  const quickLookOpenRef = useRef(false);
  useEffect(() => {
    quickLookOpenRef.current = !!quickLook || quickLookLoading;
  }, [quickLook, quickLookLoading]);

  const handlePreviewHover = useCallback((line: ProjectMoneyLine | null) => {
    previewHoverLineRef.current = line;
  }, []);

  const [amountEditBill, setAmountEditBill] = useState<SupplierBill | null>(null);
  const [termsEditBill, setTermsEditBill] = useState<SupplierBill | null>(null);
  const [amountEditValue, setAmountEditValue] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [upFile, setUpFile] = useState<File | null>(null);
  const [upVendor, setUpVendor] = useState('');
  const [upAmount, setUpAmount] = useState('');
  const [upCategory, setUpCategory] = useState<SupplierBillCategory>('fasteners');
  const [upDescription, setUpDescription] = useState('');
  const [upDealId, setUpDealId] = useState('');
  const [upBucket, setUpBucket] = useState<ProjectPLBucket | ''>('');
  const [upDueDate, setUpDueDate] = useState('');
  const [uploading, setUploading] = useState(false);

  const meName = user?.name || user?.email || 'User';

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'salesOrders'),
      (snap) => {
        setSalesOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SalesOrder) })));
        setLoaded(true);
      },
      () => setLoaded(true),
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'qbInvoices'), (snap) => {
      setQbInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as QBInvoice) })));
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'changeOrders'), where('status', '==', 'approved'));
    const unsub = onSnapshot(q, (snap) => {
      setChangeOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ChangeOrder) })));
    });
    return unsub;
  }, []);

  useEffect(() => subscribeBillingReadyItems(setTrackedItems), []);

  useEffect(() => {
    if (!canReviewInvoices) {
      setPendingInvoiceReviewCount(0);
      return undefined;
    }
    return subscribePendingReviewInvoices((list) => {
      setPendingInvoiceReviewCount(list.length);
    });
  }, [canReviewInvoices]);

  useEffect(() => {
    const unsub = subscribeSupplierBills(
      (rows) => {
        setBills(rows);
        setListenError(null);
      },
      (err) => {
        firestoreSnapshotListenError('BillingPayablesPage', 'supplierBills', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        });
        const code =
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code?: string }).code)
            : '';
        if (code === 'permission-denied') {
          setListenError(
            'Cannot load supplier bills — your account may be missing CFO or Executive Assistant designation in User Management.',
          );
        } else {
          setListenError('Cannot load supplier bills. Try refreshing the page.');
        }
      },
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    return subscribeJobPurchaseRequisitions(
      setJobPurchaseOrders,
      (err) => {
        firestoreSnapshotListenError('BillingPayablesPage', 'purchaseRequisitions', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        });
        setJobPurchaseOrders([]);
      },
    );
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    return subscribeOverheadPurchaseRequisitions(
      setOverheadPurchaseOrders,
      (err) => {
        firestoreSnapshotListenError('BillingPayablesPage', 'purchaseRequisitions.overhead', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        });
        setOverheadPurchaseOrders([]);
      },
    );
  }, [user?.id, user?.role, user?.email]);

  const baseSalesOrders = useMemo(
    () => salesOrders.filter((so) => !so.isChangeOrder),
    [salesOrders],
  );

  const coSalesOrders = useMemo(
    () => salesOrders.filter((so) => so.isChangeOrder),
    [salesOrders],
  );

  const dealOptions: QBDealOption[] = useMemo(
    () =>
      enhancedDeals
        .filter((d) => d.id && d.outcome === 'won')
        .map((d) => {
          const label = getProjectDisplayName(d) || d.title || d.id!;
          return { id: d.id!, label, searchText: `${label} ${d.contactName || ''}`.toLowerCase() };
        }),
    [enhancedDeals],
  );

  const activeProjectDealIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of timberProjects) {
      if (!p.dealId || p.status === 'completed') continue;
      if (isDealInDefaultBillingProjectScope(p.dealId, timberProjects, enhancedDeals)) {
        ids.add(p.dealId);
      }
    }
    return ids;
  }, [timberProjects, enhancedDeals]);

  const moneyInRows = useMemo(
    () =>
      buildReadyToBillQueue({
        salesOrders: baseSalesOrders,
        coSalesOrders,
        qbInvoices,
        deals: enhancedDeals,
        timberProjects,
        milestones,
        changeOrders,
        trackedItems,
        projectTasks: scheduleTasks,
        taskGroups,
        paymentLagSettings,
        forProjectSummary: true,
      }),
    [baseSalesOrders, coSalesOrders, qbInvoices, enhancedDeals, timberProjects, milestones, changeOrders, trackedItems, scheduleTasks, taskGroups, paymentLagSettings],
  );

  const queue = useMemo(
    () =>
      buildReadyToBillQueue({
        salesOrders: baseSalesOrders,
        coSalesOrders,
        qbInvoices,
        deals: enhancedDeals,
        timberProjects,
        milestones,
        changeOrders,
        trackedItems,
        projectTasks: scheduleTasks,
        taskGroups,
        paymentLagSettings,
      }),
    [baseSalesOrders, coSalesOrders, qbInvoices, enhancedDeals, timberProjects, milestones, changeOrders, trackedItems, scheduleTasks, taskGroups, paymentLagSettings],
  );

  const visibleQueue = useMemo(() => {
    const scoped = queue.filter((r) => activeProjectDealIds.has(r.dealId));
    return scoped.filter((r) => !shouldHideReadyToBillRow(r, false));
  }, [queue, activeProjectDealIds]);

  const billingSummaryInputs = useMemo(
    () => ({
      deals: enhancedDeals,
      timberProjects,
      salesOrders: baseSalesOrders,
      qbInvoices,
      moneyInRows,
      supplierBills: bills,
      rfqSubmissions,
      purchaseRequisitions: jobPurchaseOrders,
      overheadPurchaseRequisitions: overheadPurchaseOrders,
      rfqProjects,
      supplierOrgs,
      showDismissedReceivables: false,
      taskEndDateById,
    }),
    [
      enhancedDeals,
      timberProjects,
      baseSalesOrders,
      qbInvoices,
      moneyInRows,
      bills,
      rfqSubmissions,
      jobPurchaseOrders,
      overheadPurchaseOrders,
      rfqProjects,
      supplierOrgs,
      taskEndDateById,
    ],
  );

  const workingSummaries = useMemo(
    () => buildProjectBillingSummaries(billingSummaryInputs),
    [billingSummaryInputs],
  );

  const allProjectSummaries = useMemo(
    () => buildProjectBillingSummaries({ ...billingSummaryInputs, activeProjectsOnly: false }),
    [billingSummaryInputs],
  );

  const [showHistory, setShowHistory] = useState(false);

  const [statusFilter, setStatusFilter] = useState<BpStatusFilter>('all');
  const historyVisible = showHistory || statusFilter === 'paid';

  const projectSummaries = useMemo(() => {
    const workingIds = new Set(workingSummaries.map((summary) => summary.dealId));
    const source = historyVisible ? allProjectSummaries : workingSummaries;
    return source
      .map((summary) => ({
        ...summary,
        lines: summary.lines.filter((line) => {
          const history = isBillingHistoryLine(line);
          if (!historyVisible && history) return false;
          if (historyVisible && !workingIds.has(summary.dealId) && !history) return false;
          return true;
        }),
      }))
      .filter(
        (summary) => summary.lines.length > 0
          || (isOverheadBillingDealId(summary.dealId) && workingIds.has(summary.dealId)),
      );
  }, [allProjectSummaries, historyVisible, workingSummaries]);

  useEffect(() => {
    if ((scrollBillId || scrollItemId || scrollDealId) && statusFilter !== 'all') setStatusFilter('all');
  }, [scrollBillId, scrollItemId, scrollDealId, statusFilter]);

  /** Matching-line counts per filter chip (computed before filtering). */
  const filterCounts = useMemo(() => {
    const counts = new Map<BpStatusFilter, number>();
    for (const f of BP_STATUS_FILTERS) counts.set(f.id, 0);
    for (const summary of allProjectSummaries) {
      for (const line of summary.lines) {
        for (const f of BP_STATUS_FILTERS) {
          if (f.id === 'all') continue;
          if (lineMatchesStatusFilter(line, f.id)) {
            counts.set(f.id, (counts.get(f.id) ?? 0) + 1);
          }
        }
      }
    }
    return counts;
  }, [allProjectSummaries]);

  const historyCount = useMemo(
    () => allProjectSummaries.reduce(
      (sum, summary) => sum + summary.lines.filter((line) => isBillingHistoryLine(line)).length,
      0,
    ),
    [allProjectSummaries],
  );

  useEffect(() => {
    if (!scrollBillId) return;
    const inHistory = allProjectSummaries.some((summary) =>
      summary.lines.some(
        (line) => isBillingHistoryLine(line) && line.supplierBill?.id === scrollBillId,
      ),
    );
    if (inHistory) setShowHistory(true);
  }, [allProjectSummaries, scrollBillId]);

  const visibleSummaries = useMemo(() => {
    return projectSummaries
      .map((summary) => {
        const lines = summary.lines.filter((line) => lineMatchesStatusFilter(line, statusFilter));
        const hideOpenQuotes = statusFilter !== 'quotes';
        return {
          ...summary,
          lines,
          totals: hideOpenQuotes ? { ...summary.totals, openQuotes: 0 } : summary.totals,
        };
      })
      .filter(
        (summary) => summary.lines.length > 0 || isOverheadBillingDealId(summary.dealId),
      );
  }, [projectSummaries, statusFilter]);

  const pinnedOverheadSummary = useMemo(
    () => visibleSummaries.find((s) => isOverheadBillingDealId(s.dealId)) ?? null,
    [visibleSummaries],
  );

  const projectSummariesBelow = useMemo(
    () => visibleSummaries.filter((s) => !isOverheadBillingDealId(s.dealId)),
    [visibleSummaries],
  );

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterMenuOpen && !printMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterMenuOpen && filterMenuRef.current && !filterMenuRef.current.contains(target)) {
        setFilterMenuOpen(false);
      }
      if (printMenuOpen && printMenuRef.current && !printMenuRef.current.contains(target)) {
        setPrintMenuOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setFilterMenuOpen(false);
      setPrintMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [filterMenuOpen, printMenuOpen]);

  /**
   * AR / AP totals for what is currently on screen.
   * - Default: outstanding amounts only — paid invoices/bills are excluded and
   *   partially paid bills count just their remaining balance.
   * - "Paid" flips to amounts actually paid.
   * - Payment-state filters count only the matching installment slices.
   * - Open (unawarded) quotes only count under the "Open quotes" filter;
   *   approved quotes are committed spend and always count.
   */
  const filteredTotals = useMemo(() => {
    let ar = 0;
    let ap = 0;
    const perProject: Array<{ dealId: string; label: string; ar: number; ap: number }> = [];
    const paidFilter = statusFilter === 'paid';
    for (const summary of visibleSummaries) {
      let projAr = 0;
      let projAp = 0;
      for (const line of summary.lines) {
        if (line.direction === 'in') {
          if (paidFilter) {
            if (line.statusKind === 'paid') projAr += line.amount;
          } else if (line.statusKind !== 'paid' && line.statusKind !== 'dismissed') {
            projAr += line.amount;
          }
          continue;
        }
        const bill = line.supplierBill;
        if (bill) {
          if (
            isPaymentStateFilter(statusFilter)
            && bill.paymentTerms?.installments?.length
          ) {
            const slices = expandSupplierBillPayments(bill, { taskEndDateById }).filter(
              (s) => s.installmentIndex !== undefined,
            );
            for (const slice of slices) {
              if (installmentSliceMatchesFilter(bill, slice, statusFilter)) projAp += slice.amount;
            }
          } else if (paidFilter) {
            projAp += supplierBillPaidAmount(bill);
          } else {
            projAp += Math.max(
              0,
              roundMoney(Number(bill.amount) || 0) - supplierBillPaidAmount(bill),
            );
          }
          continue;
        }
        if (line.direction === 'quote') {
          if (line.statusKind === 'ready' || statusFilter === 'quotes') projAp += line.amount;
        }
      }
      ar += projAr;
      ap += projAp;
      if (projAr > 0 || projAp > 0) {
        perProject.push({ dealId: summary.dealId, label: summary.dealLabel, ar: projAr, ap: projAp });
      }
    }
    return { ar, ap, perProject };
  }, [visibleSummaries, statusFilter, taskEndDateById]);

  const handleDownloadReport = useCallback((ledger: BillingPayablesLedger) => {
    setPrintMenuOpen(false);
    const filterLabel = BP_STATUS_FILTERS.find((f) => f.id === statusFilter)?.label ?? 'All';
    void exportBillingPayablesPdf({
      summaries: visibleSummaries,
      totals: { ar: filteredTotals.ar, ap: filteredTotals.ap },
      filterLabel,
      statusFilter,
      ledger,
      taskEndDateById,
    });
  }, [visibleSummaries, filteredTotals, statusFilter, taskEndDateById]);

  /** Hub project per deal — powers the "Suppliers" jump-back button on each card. */
  const hubProjectIdByDeal = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of timberProjects) {
      if (p.id && p.dealId && !m.has(p.dealId)) m.set(p.dealId, p.id);
    }
    return m;
  }, [timberProjects]);

  // Arriving via the Project Hub "B" hotkey (?deal=...) or an approved-quote
  // payment link (?bill=...) scrolls to that project / bill row.
  const scrolledDealRef = useRef<string | null>(null);
  const focusDealId = scrollDealId
    || trackedItems.find((item) => item.id === scrollItemId)?.dealId
    || null;
  useEffect(() => {
    const focusKey = `${focusDealId || ''}:${scrollBillId || ''}:${scrollItemId || ''}`;
    if (!focusDealId && !scrollBillId && !scrollItemId) return;
    if (scrolledDealRef.current === focusKey) return;
    if (focusDealId && !projectSummaries.some((s) => s.dealId === focusDealId)) return;
    if (
      scrollBillId
      && !projectSummaries.some((s) => s.lines.some((line) => line.supplierBill?.id === scrollBillId))
    ) {
      return;
    }
    const readyEl = scrollItemId ? document.getElementById(`bp-ready-${scrollItemId}`) : null;
    const billEl = (scrollBillId || scrollItemId)
      ? document.getElementById(`bp-bill-${scrollBillId || scrollItemId}`)
      : null;
    const dealEl = focusDealId ? document.getElementById(`bp-project-${focusDealId}`) : null;
    const el = readyEl || billEl || dealEl;
    if (!el) return;
    scrolledDealRef.current = focusKey;
    const onRow = !!(readyEl || billEl);
    el.scrollIntoView({ behavior: 'smooth', block: onRow ? 'center' : 'start' });
    const flashClass = onRow ? 'bp-line-row--flash' : 'bp-project-card--flash';
    el.classList.add(flashClass);
    const timer = setTimeout(() => el.classList.remove(flashClass), 2500);
    return () => clearTimeout(timer);
  }, [focusDealId, scrollBillId, scrollItemId, projectSummaries]);

  const billableNow = visibleQueue.filter((r) => r.billableNow);

  const openApCount = useMemo(
    () => bills.filter((bill) => bill.status !== 'paid' && bill.status !== 'void').length,
    [bills],
  );

  const finishMarkReady = useCallback(
    async (row: ReadyToBillRow, partial?: PartialInvoiceSelection | null) => {
      if (!user?.id) return;
      setBusyKey(row.key);
      setError(null);
      try {
        const salesOrder = row.salesOrderId
          ? salesOrders.find((so) => so.id === row.salesOrderId)
          : undefined;
        let status: BillingReadyStatus = 'ready';
        let quickbooksInvoiceNum: string | undefined;
        const partialInvoice = !!partial && (
          (partial.structureIds?.length ?? 0) > 0 || Array.isArray(partial.foldChangeOrderRefs)
        );
        const canCreate = row.salesOrderId && (
          shouldAutoCreateProgressInvoice({
            kind: row.kind,
            installmentId: row.installmentId,
            salesOrder,
          }) || partialInvoice
        );
        if (canCreate && row.salesOrderId) {
          const created = await triggerQuickBooksInvoiceForSalesOrder(
            row.salesOrderId,
            row.installmentId,
            partialInvoice && partial
              ? {
                structureIds: partial.structureIds,
                structureGroupKeys: partial.structureGroupKeys,
                structureGroupLabels: partial.structureGroupLabels,
                omittedPackageLabels: partial.omittedPackageLabels,
                structureGroups: partial.structureGroups,
                foldChangeOrderRefs: partial.foldChangeOrderRefs,
              }
              : undefined,
          );
          if (created.ok) {
            status = 'invoiced';
            quickbooksInvoiceNum = created.invoiceNumber;
          } else {
            setError(created.error || 'QuickBooks invoice was not created. The milestone is still marked ready.');
          }
        }
        await markReadyToBill({
          row: {
            dealId: row.dealId,
            salesOrderId: row.salesOrderId,
            installmentId: row.installmentId,
            changeOrderId: row.changeOrderId,
            kind: row.kind,
            label: partial?.structureGroupLabels?.length
              ? `${row.label} — ${partial.structureGroupLabels.join(', ')}`
              : row.label,
            detail: `${row.dealLabel} — ${row.detail}`,
            amount: partial?.amount ?? row.amount,
          },
          byUserId: user.id,
          byUserName: meName,
          status,
          quickbooksInvoiceNum,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to mark ready');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName, salesOrders],
  );

  const handleMarkReady = useCallback(
    async (row: ReadyToBillRow) => {
      if (!user?.id) return;
      if (row.qbInvoiced && !invoiceFinanceApproved(row)) {
        setBusyKey(row.key);
        setError(null);
        try {
          await markReadyToBill({
            row: {
              dealId: row.dealId,
              salesOrderId: row.salesOrderId,
              installmentId: row.installmentId,
              changeOrderId: row.changeOrderId,
              kind: row.kind,
              label: row.label,
              detail: row.detail,
              amount: row.amount,
            },
            byUserId: user.id,
            byUserName: meName,
            status: 'invoiced',
            quickbooksInvoiceNum: row.quickbooksInvoiceNum,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to approve the invoice');
        } finally {
          setBusyKey(null);
        }
        return;
      }
      const salesOrder = row.salesOrderId
        ? salesOrders.find((so) => so.id === row.salesOrderId)
        : undefined;
      const splittable = row.installmentId === 'pre_shipment' || row.installmentId === 'final';
      if (!splittable || !salesOrder || row.kind !== 'installment') {
        await finishMarkReady(row);
        return;
      }
      setBusyKey(row.key);
      setError(null);
      try {
        const loaded = await loadBillablePackagesForDeal(row.dealId, salesOrder.lineItems, row.projectId);
        const scheduleForChoice = buildPaymentScheduleBreakdown(
          salesOrder.grandTotal || 0,
          changeOrders.filter((co) => co.dealId === row.dealId),
          {
            invoicedInstallments: invoicedInstallmentLocks(salesOrder.installmentInvoices),
          },
        );
        const choiceRow = scheduleForChoice.rows.find((item) => item.id === row.installmentId);
        const choice = milestonePackageChoices({
          installmentId: row.installmentId === 'final' ? 'final' : 'pre_shipment',
          packages: loaded.packages,
          structures: loaded.structures,
          invoices: salesOrder.installmentInvoices || [],
          changeOrders: choiceRow?.coAdjustments || [],
        });
        if (choice.mode !== 'pick') {
          if (
            choice.mode === 'full'
            && choice.packages.length > 0
            && choice.packages.every((item) => item.disabledReason)
          ) {
            setError(choice.packages.find((item) => item.disabledReason)?.disabledReason
              || 'Bill the pre-shipment invoice for this package first.');
            return;
          }
          const pkg = choice.mode === 'single'
            ? choice.packages.find((item) => !item.disabledReason)
            : undefined;
          let partial: PartialInvoiceSelection | null = null;
          if (choice.mode === 'single' && pkg && row.installmentId) {
            const schedule = buildPaymentScheduleBreakdown(
              salesOrder.grandTotal || 0,
              changeOrders.filter((co) => co.dealId === row.dealId),
              {
                invoicedInstallments: invoicedInstallmentLocks(salesOrder.installmentInvoices),
              },
            );
            const schedRow = schedule.rows.find((item) => item.id === row.installmentId);
            const omitted = choice.packages
              .filter((item) => item.key !== pkg.key && !item.disabledReason)
              .map((item) => item.label);
            const allocated = allocateMilestoneSlice({
              installmentId: row.installmentId,
              baseAmount: schedRow?.baseAmount ?? row.amount,
              milestoneAmount: schedRow?.amount ?? row.amount,
              coAdjustments: schedRow?.coAdjustments || [],
              structures: loaded.structures,
              selectedStructureIds: choice.structureIds,
              existingInvoices: salesOrder.installmentInvoices || [],
              includedLabels: [pkg.label],
              includedGroupKeys: [pkg.key],
              omittedLabels: omitted,
              milestoneLabel: row.label,
            });
            partial = {
              structureIds: choice.structureIds,
              structureGroupKeys: [pkg.key],
              structureGroupLabels: [pkg.label],
              omittedPackageLabels: omitted,
              structureGroups: [{ key: pkg.key, label: pkg.label, structureIds: pkg.structureIds }],
              amount: allocated.ok && !allocated.slice.fullMilestone ? allocated.slice.amount : undefined,
            };
            if (allocated.ok && allocated.slice.fullMilestone) partial = null;
          }
          await finishMarkReady(row, partial);
          return;
        }
        const schedule = buildPaymentScheduleBreakdown(
          salesOrder.grandTotal || 0,
          changeOrders.filter((co) => co.dealId === row.dealId),
          {
            invoicedInstallments: invoicedInstallmentLocks(salesOrder.installmentInvoices),
          },
        );
        const schedRow = schedule.rows.find((item) => item.id === row.installmentId);
        setPartialPicker({
          row,
          structures: loaded.structures,
          packages: choice.packages,
          baseAmount: schedRow?.baseAmount ?? row.amount,
          milestoneAmount: schedRow?.amount ?? row.amount,
          coAdjustments: schedRow?.coAdjustments || [],
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load structure groups');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, salesOrders, changeOrders, finishMarkReady],
  );

  /**
   * EA records that an invoice was emailed to the client. Rows invoiced
   * directly in QB may have no tracking doc yet — create one first.
   */
  const handleMarkSent = useCallback(
    async (row: ReadyToBillRow) => {
      if (!user?.id) return;
      setBusyKey(row.key);
      setError(null);
      try {
        let itemId = row.tracked?.id;
        if (!itemId) {
          itemId = await markReadyToBill({
            row: {
              dealId: row.dealId,
              salesOrderId: row.salesOrderId,
              installmentId: row.installmentId,
              changeOrderId: row.changeOrderId,
              kind: row.kind,
              label: row.label,
              detail: row.detail,
              amount: row.amount,
            },
            byUserId: user.id,
            byUserName: meName,
          });
        }
        await updateBillingReadyItemStatus({
          itemId,
          status: 'sent',
          byUserId: user.id,
          byUserName: meName,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to mark sent to client');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName],
  );

  const handleBillQbRef = useCallback(
    async (bill: SupplierBill, note: string) => {
      if (!bill.id) return;
      setBusyKey(`${bill.id}:qbref`);
      setError(null);
      try {
        await setSupplierBillQbReference(bill.id, note);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update the QB reference');
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const handleStepBackInvoice = useCallback(
    async (item: BillingReadyItem) => {
      if (!user?.id || !item.id) return;
      const prev = previousBillingReadyStatus(item.status);
      if (!prev) return;
      setBusyKey(item.id);
      setError(null);
      try {
        if (prev === 'forecast') {
          await deleteBillingReadyItem(item.id);
          return;
        }
        await updateBillingReadyItemStatus({
          itemId: item.id,
          status: prev,
          byUserId: user.id,
          byUserName: meName,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to step the invoice back');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName],
  );

  const handleTrackedStatus = useCallback(
    async (item: BillingReadyItem, status: BillingReadyStatus) => {
      if (!user?.id || !item.id) return;
      setBusyKey(item.id);
      setError(null);
      try {
        await updateBillingReadyItemStatus({
          itemId: item.id,
          status,
          byUserId: user.id,
          byUserName: meName,
        });
        if (status === 'paid') {
          try {
            await markSalesOrderInstallmentCollected({
              salesOrderId: item.salesOrderId,
              installmentId: item.installmentId,
              quickbooksInvoiceNum: item.quickbooksInvoiceNum,
            });
          } catch (err) {
            console.warn('[BillingPayables] sales-order paid mark failed', err);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update status');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName],
  );

  /**
   * Backfill a supplier bill for a quote approved before the award flow
   * created bills automatically — then open the payment-terms modal so the
   * payable gets a real due-date schedule.
   */
  const handleRecordBill = useCallback(
    async (line: ProjectMoneyLine) => {
      if (!user?.id) return;
      const prId = line.purchaseRequisitionId?.trim();
      if (prId && !line.supplierBill && !line.rfqSubmissionId) {
        const pr =
          overheadPurchaseOrders.find((row) => row.id === prId)
          || jobPurchaseOrders.find((row) => row.id === prId);
        if (!pr?.poNumber?.trim()) return;
        setBusyKey(line.key);
        setError(null);
        try {
          await recordAchPayableForIssuedPurchase({
            requisition: pr,
            poNumber: pr.poNumber.trim(),
            actorId: user.id,
            actorName: meName,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to create payable');
        } finally {
          setBusyKey(null);
        }
        return;
      }
      if (!line.rfqSubmissionId) return;
      const sub = rfqSubmissions.find((s) => s.id === line.rfqSubmissionId);
      if (!sub?.id) return;
      const project = rfqProjects.find((p) => p.id === sub.rfqProjectId);
      if (!project?.id) return;
      const orgName =
        supplierOrgs.find((o) => o.id === sub.supplierOrgId)?.name || 'Supplier';
      const category =
        sub.workType === 'cnc_quote' ? 'cnc' : sub.workType === 'other_quote' ? 'other' : 'timber';
      const submissionKey =
        category === 'cnc' ? cncRfqAwardSubmissionKey(sub.supplierOrgId) : sub.id;
      setBusyKey(line.key);
      setError(null);
      try {
        await recordSupplierBillForRfqAward(
          {
            project,
            category,
            orgId: sub.supplierOrgId,
            orgName,
            submissionKey,
            amount: Number(sub.quoteTotal) || 0,
            description: line.label,
            byUserId: user.id,
            byUserName: meName,
          },
          { throwOnError: true },
        );
        const bill =
          (await findSupplierBillForRfqAward(project.id, submissionKey))
          || (category === 'cnc'
            ? await findSupplierBillForCncOrgAward(project.id, sub.supplierOrgId)
            : null);
        if (bill) setTermsEditBill(bill);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to record bill');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName, rfqSubmissions, rfqProjects, supplierOrgs, overheadPurchaseOrders, jobPurchaseOrders],
  );

  const openPayConfirm = useCallback((bill: SupplierBill, installmentIndex: number | null) => {
    const count = bill.paymentTerms?.installments.length ?? 0;
    const idx = installmentIndex != null ? installmentIndex : (count === 1 ? 0 : null);
    const taskId = idx != null ? bill.paymentTerms?.installments[idx]?.taskId : undefined;
    const suggestion = suggestEarlyPayAmount({
      bill,
      installmentIndex: idx,
      paidAt: new Date(),
      taskEndDate: taskId ? taskEndDateById.get(taskId) ?? null : null,
    });
    setPayConfirm({ bill, installmentIndex: idx, suggestion });
    setPayAmountInput(suggestion.suggestedAmount.toFixed(2));
    setError(null);
  }, [taskEndDateById]);

  const handleBillStatus = useCallback(
    async (bill: SupplierBill, to: SupplierBillStatus, note?: string) => {
      if (!user?.id || !bill.id) return;
      if (to === 'paid') {
        const count = bill.paymentTerms?.installments.length ?? 0;
        if (count > 1) {
          setError('Mark each payment paid on its own row.');
          return;
        }
        openPayConfirm(bill, count === 1 ? 0 : null);
        return;
      }
      setBusyKey(bill.id);
      setError(null);
      try {
        await updateSupplierBillStatus({
          bill,
          to,
          byUserId: user.id,
          byUserName: meName,
          qbReferenceNote: note,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update bill');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName, openPayConfirm],
  );

  const handleInstallmentPaid = useCallback(
    async (bill: SupplierBill, installmentIndex: number, paid: boolean) => {
      if (!user?.id || !bill.id) return;
      if (paid) {
        openPayConfirm(bill, installmentIndex);
        return;
      }
      setBusyKey(`${bill.id}:inst:${installmentIndex}`);
      setError(null);
      try {
        await setSupplierBillInstallmentPayment({
          bill,
          installmentIndex,
          paid,
          byUserId: user.id,
          byUserName: meName,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update installment payment');
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName, openPayConfirm],
  );

  const confirmPayAmount = useCallback(async () => {
    const billId = payConfirm?.bill.id;
    if (!user?.id || !payConfirm || !billId) return;
    const amount = parsePaidAmountInput(payAmountInput);
    if (amount == null) {
      setError('Enter the amount paid.');
      return;
    }
    const { bill, installmentIndex } = payConfirm;
    const busy = installmentIndex != null ? `${billId}:inst:${installmentIndex}` : billId;
    setBusyKey(busy);
    setError(null);
    try {
      if (installmentIndex != null) {
        await setSupplierBillInstallmentPayment({
          bill,
          installmentIndex,
          paid: true,
          byUserId: user.id,
          byUserName: meName,
          amountPaid: amount,
        });
      } else {
        await updateSupplierBillStatus({
          bill,
          to: 'paid',
          byUserId: user.id,
          byUserName: meName,
          amountPaid: amount,
        });
      }
      setPayConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setBusyKey(null);
    }
  }, [user?.id, meName, payConfirm, payAmountInput]);

  const handleInstallmentPaidDate = useCallback(
    async (bill: SupplierBill, installmentIndex: number, paidAt: Date) => {
      if (!bill.id) return;
      setBusyKey(`${bill.id}:pdate:${installmentIndex}`);
      setError(null);
      try {
        await updateSupplierBillInstallmentPaidDate({ bill, installmentIndex, paidAt });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update paid date');
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const handleInstallmentApproval = useCallback(
    async (bill: SupplierBill, installmentIndex: number, approved: boolean, note?: string) => {
      if (!user?.id || !bill.id) return false;
      setBusyKey(`${bill.id}:appr:${installmentIndex}`);
      setError(null);
      try {
        await setSupplierBillInstallmentApproval({
          bill,
          installmentIndex,
          approved,
          byUserId: user.id,
          byUserName: meName,
          note: note?.trim().slice(0, 500) || undefined,
        });
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update installment approval');
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [user?.id, meName],
  );

  const requestInstallmentApproval = useCallback(
    (bill: SupplierBill, installmentIndex: number, approved: boolean) => {
      if (!approved) {
        void handleInstallmentApproval(bill, installmentIndex, false);
        return;
      }
      setApproveNote('');
      setApprovePrompt({ bill, installmentIndex });
    },
    [handleInstallmentApproval],
  );

  const confirmInstallmentApproval = useCallback(async () => {
    if (!approvePrompt) return;
    const ok = await handleInstallmentApproval(
      approvePrompt.bill,
      approvePrompt.installmentIndex,
      true,
      approveNote,
    );
    if (ok) setApprovePrompt(null);
  }, [approvePrompt, approveNote, handleInstallmentApproval]);

  const submissionsById = useMemo(() => {
    const m = new Map<string, RfqSubmission>();
    for (const s of rfqSubmissions) if (s.id) m.set(s.id, s);
    return m;
  }, [rfqSubmissions]);

  // CNC award bills key on `org:<id>`, not a submission — fall back to the
  // org's newest quote file on that RFQ project.
  const submissionsByProjectOrg = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    for (const s of rfqSubmissions) {
      if (!s.storagePath || !s.rfqProjectId || !s.supplierOrgId) continue;
      const key = `${s.rfqProjectId}:${s.supplierOrgId}`;
      const list = m.get(key) ?? [];
      list.push(s);
      m.set(key, list);
    }
    m.forEach((list) => {
      list.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
    });
    return m;
  }, [rfqSubmissions]);

  /** The quote PDF behind a payment-side line, if one is stored. */
  const findPreviewSubmission = useCallback(
    (line: ProjectMoneyLine): RfqSubmission | null => {
      const bill = line.supplierBill;
      const subId = line.rfqSubmissionId || bill?.rfqSubmissionId;
      if (subId) {
        const sub = submissionsById.get(subId);
        if (sub?.storagePath) return sub;
      }
      if (bill?.rfqProjectId && bill.supplierOrgId) {
        const list = submissionsByProjectOrg.get(`${bill.rfqProjectId}:${bill.supplierOrgId}`);
        if (list?.[0]) return list[0];
      }
      return null;
    },
    [submissionsById, submissionsByProjectOrg],
  );

  /** Only rows with an actual stored file get the Quick Look affordance. */
  const lineHasPreview = useCallback(
    (line: ProjectMoneyLine): boolean => {
      if (line.direction === 'in') return false;
      if (line.supplierBill?.fileUrl) return true;
      return !!findPreviewSubmission(line);
    },
    [findPreviewSubmission],
  );

  const openQuickLook = useCallback(
    async (line: ProjectMoneyLine) => {
      const bill = line.supplierBill;
      setQuickLookLoading(true);
      try {
        let url: string | null = bill?.fileUrl ?? null;
        let name = bill?.fileName || line.detail || line.label;
        if (!url) {
          const sub = findPreviewSubmission(line);
          if (sub?.storagePath) {
            url = await getDownloadURL(storageRef(storage, sub.storagePath));
            name = sub.fileName || name;
          }
        }
        if (url) setQuickLook({ url, name });
      } catch {
        // Missing/inaccessible file — quietly do nothing, same as Finder.
      } finally {
        setQuickLookLoading(false);
      }
    },
    [findPreviewSubmission],
  );

  // Space bar = Quick Look toggle (like Finder): opens the PDF for the row
  // amount under the cursor, closes when already open. Esc also closes.
  // Hovering a project card arms the Project Hub view keys (T/F/S/C/L).
  // F with no card hovered jumps to Finances (Cmd/Ctrl+F stays browser Find).
  const hoverHubProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && quickLookOpenRef.current) {
        setQuickLook(null);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isEditableTarget(e.target)) return;
      const hubProjectId = hoverHubProjectIdRef.current;
      if (hubProjectId) {
        const mode = projectHubViewModeForHotkey(e.key, {
          canAccessSuppliersTab,
          canAccessChangeOrdersTab,
        });
        if (mode) {
          e.preventDefault();
          navigate(projectHubViewPath(hubProjectId, mode));
          return;
        }
      }
      if (e.code === 'KeyF') {
        e.preventDefault();
        navigate('/finances');
        return;
      }
      if (e.code !== 'Space') return;
      // A focused control (the History toggle, after it is clicked) would
      // otherwise take Space and hide history instead of previewing the row.
      if (quickLookOpenRef.current) {
        e.preventDefault();
        setQuickLook(null);
        return;
      }
      const line = previewHoverLineRef.current;
      if (line) {
        e.preventDefault();
        void openQuickLook(line);
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'BUTTON') return;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openQuickLook, navigate, canAccessSuppliersTab, canAccessChangeOrdersTab]);

  const handleSaveAmount = useCallback(async () => {
    if (!amountEditBill?.id) return;
    const amount = Number(amountEditValue);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Enter a positive amount.');
      return;
    }
    setBusyKey(amountEditBill.id);
    setError(null);
    try {
      await updateSupplierBillDetails({ billId: amountEditBill.id, amount });
      setAmountEditBill(null);
      setAmountEditValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update amount');
    } finally {
      setBusyKey(null);
    }
  }, [amountEditBill, amountEditValue]);

  const handleUpload = useCallback(async () => {
    if (!user?.id) return;
    const amount = Number(upAmount);
    if (!upVendor.trim() || !Number.isFinite(amount) || amount <= 0) {
      setError('Vendor and a positive amount are required.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      let fileMeta: { fileUrl: string; storagePath: string; fileName: string } | undefined;
      if (upFile) fileMeta = await uploadSupplierBillFile(upFile, user.id);
      await createSupplierBill({
        source: 'manual_upload',
        category: upCategory,
        vendorName: upVendor,
        description: upDescription || `${SUPPLIER_BILL_CATEGORY_LABELS[upCategory]} bill`,
        amount,
        dealId: upDealId || undefined,
        plBucket: upBucket || undefined,
        dueDate: upDueDate ? new Date(`${upDueDate}T12:00:00`) : undefined,
        ...fileMeta,
        byUserId: user.id,
        byUserName: meName,
      });
      setUploadOpen(false);
      setUpFile(null);
      setUpVendor('');
      setUpAmount('');
      setUpDescription('');
      setUpDealId('');
      setUpBucket('');
      setUpDueDate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save bill');
    } finally {
      setUploading(false);
    }
  }, [user?.id, meName, upFile, upVendor, upAmount, upCategory, upDescription, upDealId, upBucket, upDueDate]);

  if (authLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canView) {
    return (
      <div className="fin-hub">
        <AppHeader />
        <p className="bp-access-note">You don’t have access to Billing &amp; Payables.</p>
      </div>
    );
  }

  return (
    <div className="fin-hub">
      <AppHeader />
      <div className="bp-fin-main">
        <div className="bp-page-head">
          <div className="fin-hub-toolbar bp-toolbar">
            <div className="bp-toolbar-main">
              <h1 className="fin-hub-title">Billing &amp; Payables</h1>
              <div className="bp-toolbar-stats">
                <span className="fin-stat">
                  <span className="fin-stat-strong">{billableNow.length}</span> AR to bill
                </span>
                <span className="fin-stat">
                  <span className="fin-stat-strong">{openApCount}</span> AP open
                </span>
                <div
                  ref={printMenuRef}
                  className={`bp-print-dd${printMenuOpen ? ' bp-print-dd--open' : ''}`}
                >
                  <button
                    type="button"
                    className="fin-icon-btn bp-print-btn"
                    aria-label="Print report"
                    aria-haspopup="menu"
                    aria-expanded={printMenuOpen}
                    title="Print report"
                    onClick={() => setPrintMenuOpen((open) => !open)}
                  >
                    <Printer size={14} />
                    <ChevronDown size={11} className="bp-print-chevron" />
                  </button>
                  {printMenuOpen && (
                    <div className="bp-print-menu pm-dropdown" role="menu" aria-label="Print report">
                      <button
                        type="button"
                        role="menuitem"
                        className="pm-dropdown-item"
                        onClick={() => handleDownloadReport('ar')}
                      >
                        Accounts receivable
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="pm-dropdown-item"
                        onClick={() => handleDownloadReport('ap')}
                      >
                        Accounts payable
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="pm-dropdown-item"
                        onClick={() => handleDownloadReport('all')}
                      >
                        All
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="fin-hub-content">
          {canReviewInvoices && pendingInvoiceReviewCount > 0 && (
            <Link
              to="/invoice-review"
              className="bp-invoice-review-banner"
              aria-label={`${pendingInvoiceReviewCount} invoices need review`}
            >
              {pendingInvoiceReviewCount === 1
                ? '1 invoice needs review'
                : `${pendingInvoiceReviewCount} invoices need review`}
            </Link>
          )}
          <div className="bp-hub-row">
            <button
              type="button"
              className="bp-invoice-review-banner"
              onClick={() => setShowBillingsHub(true)}
            >
              Billings Hub
              {pendingInvoiceReviewCount > 0
                ? ` · ${pendingInvoiceReviewCount} to sort`
                : ''}
            </button>
            <button
              type="button"
              className="bp-flow-launch"
              aria-label="How a bill gets paid"
              title="How a bill gets paid"
              onClick={() => setShowPayFlow(true)}
            >
              <GitBranch size={14} aria-hidden />
            </button>
          </div>
          {loaded && allProjectSummaries.length > 0 && (
            <div className="bp-filter-bar">
              <div
                ref={filterMenuRef}
                className={`bp-filter-dd${filterMenuOpen ? ' bp-filter-dd--open' : ''}`}
              >
                <button
                  type="button"
                  className={`bp-filter-dd-btn${statusFilter !== 'all' ? ' bp-filter-dd-btn--active' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={filterMenuOpen}
                  onClick={() => setFilterMenuOpen((v) => !v)}
                >
                  <ListFilter size={13} />
                  {BP_STATUS_FILTERS.find((f) => f.id === statusFilter)?.label ?? 'All'}
                  {statusFilter !== 'all' && (
                    <span className="bp-filter-count">{filterCounts.get(statusFilter) ?? 0}</span>
                  )}
                  <ChevronDown size={12} className="bp-filter-dd-chevron" />
                </button>
                {filterMenuOpen && (
                  <div className="bp-filter-menu pm-dropdown" role="menu">
                    {BP_STATUS_FILTERS.map((f) => {
                      const count = f.id === 'all' ? null : filterCounts.get(f.id) ?? 0;
                      const disabled = count === 0;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={statusFilter === f.id}
                          className={`pm-dropdown-item bp-filter-menu-item${statusFilter === f.id ? ' bp-filter-menu-item--active' : ''}`}
                          disabled={disabled}
                          onClick={() => {
                            setStatusFilter(f.id);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="bp-filter-menu-check">
                            {statusFilter === f.id && <Check size={12} />}
                          </span>
                          {f.label}
                          {count !== null && <span className="bp-filter-count">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={`bp-filter-dd-btn${historyVisible ? ' bp-filter-dd-btn--active' : ''}`}
                aria-expanded={historyVisible}
                onClick={(event) => {
                  event.currentTarget.blur();
                  if (historyVisible) {
                    setShowHistory(false);
                    if (statusFilter === 'paid') setStatusFilter('all');
                  } else {
                    setShowHistory(true);
                  }
                }}
              >
                History
                {historyCount > 0 && <span className="bp-filter-count">{historyCount}</span>}
              </button>
              <HoverTip
                wide
                tip={
                  <div className="bp-tip-breakdown">
                    <div className="bp-tip-breakdown-title">
                      {statusFilter === 'paid' ? 'Receivables collected' : 'Receivables outstanding'}
                    </div>
                    {filteredTotals.perProject
                      .filter((p) => p.ar > 0)
                      .sort((a, b) => b.ar - a.ar)
                      .map((p) => (
                        <div key={p.dealId} className="bp-tip-breakdown-row">
                          <span className="bp-tip-breakdown-name">{p.label}</span>
                          <span className="bp-tip-breakdown-amt">{fmt(p.ar)}</span>
                        </div>
                      ))}
                  </div>
                }
              >
                <span className="bp-filter-total bp-filter-total--ar">
                  <span className="bp-filter-total-label">AR</span>
                  {fmt(filteredTotals.ar)}
                </span>
              </HoverTip>
              <HoverTip
                wide
                tip={
                  <div className="bp-tip-breakdown">
                    <div className="bp-tip-breakdown-title">
                      {statusFilter === 'paid' ? 'Payables paid' : 'Payables open'}
                    </div>
                    {filteredTotals.perProject
                      .filter((p) => p.ap > 0)
                      .sort((a, b) => b.ap - a.ap)
                      .map((p) => (
                        <div key={p.dealId} className="bp-tip-breakdown-row">
                          <span className="bp-tip-breakdown-name">{p.label}</span>
                          <span className="bp-tip-breakdown-amt">{fmt(p.ap)}</span>
                        </div>
                      ))}
                  </div>
                }
              >
                <span className="bp-filter-total bp-filter-total--ap">
                  <span className="bp-filter-total-label">AP</span>
                  {fmt(filteredTotals.ap)}
                </span>
              </HoverTip>
            </div>
          )}

          {error && (
            <div className="fin-balance-error" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={14} />
              {error}
              <button type="button" onClick={() => setError(null)} aria-label="Dismiss error" style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer' }}>
                <X size={13} />
              </button>
            </div>
          )}

          {listenError && (
            <div className="fin-balance-error" style={{ marginBottom: 12 }}>
              {listenError}
            </div>
          )}

          {!loaded ? (
            <p className="fin-balance-empty">Loading…</p>
          ) : projectSummaries.length === 0 ? (
            <p className="fin-balance-empty">
              {historyCount > 0 && !historyVisible
                ? 'No open billing. History stays hidden until you show it.'
                : 'No active billing on progress-billing projects, supplier bills, or open vendor quotes.'}
            </p>
          ) : visibleSummaries.length === 0 && !pinnedOverheadSummary ? (
            <p className="fin-balance-empty">
              Nothing matches the “{BP_STATUS_FILTERS.find((f) => f.id === statusFilter)?.label}”
              filter.
            </p>
          ) : (
            <>
              {pinnedOverheadSummary ? (
                <div className="bp-overhead-pinned">
                  <ProjectSummaryCard
                    key={pinnedOverheadSummary.dealId}
                    summary={pinnedOverheadSummary}
                    cardClassName="bp-project-card--overhead"
                    canMarkReady={canMarkReady}
                    canManage={canManage}
                    canApprove={canApprove}
                    busyKey={busyKey}
                    statusFilter={statusFilter}
                    onMarkReady={handleMarkReady}
                    onMarkSent={handleMarkSent}
                    onTrackedStatus={handleTrackedStatus}
                    onStepBackInvoice={handleStepBackInvoice}
                    onBillStatus={handleBillStatus}
                    onBillQbModal={(bill) => {
                      setQbModalBill(bill);
                      setQbNote(bill.qbReferenceNote || '');
                    }}
                    onBillQbRef={handleBillQbRef}
                    onBillAmountEdit={(bill) => {
                      setAmountEditBill(bill);
                      setAmountEditValue('');
                    }}
                    onBillTermsEdit={(bill) => setTermsEditBill(bill)}
                    onRecordBill={handleRecordBill}
                    onInstallmentPaid={handleInstallmentPaid}
                    onInstallmentApproval={requestInstallmentApproval}
                    onInstallmentPaidDate={handleInstallmentPaidDate}
                    onPreviewHover={handlePreviewHover}
                    lineHasPreview={lineHasPreview}
                    taskEndDateById={taskEndDateById}
                    hubProjectId={null}
                    onHubHover={(projectId) => {
                      hoverHubProjectIdRef.current = projectId;
                    }}
                    onOpenSuppliers={null}
                  />
                </div>
              ) : null}
              {projectSummariesBelow.length > 0 ? (
                <div className="bp-project-list">
                  {projectSummariesBelow.map((summary) => (
                    <ProjectSummaryCard
                      key={summary.dealId}
                      summary={summary}
                      canMarkReady={canMarkReady}
                      canManage={canManage}
                      canApprove={canApprove}
                      busyKey={busyKey}
                      statusFilter={statusFilter}
                      onMarkReady={handleMarkReady}
                      onMarkSent={handleMarkSent}
                      onTrackedStatus={handleTrackedStatus}
                      onStepBackInvoice={handleStepBackInvoice}
                      onBillStatus={handleBillStatus}
                      onBillQbModal={(bill) => {
                        setQbModalBill(bill);
                        setQbNote(bill.qbReferenceNote || '');
                      }}
                      onBillQbRef={handleBillQbRef}
                      onBillAmountEdit={(bill) => {
                        setAmountEditBill(bill);
                        setAmountEditValue('');
                      }}
                      onBillTermsEdit={(bill) => setTermsEditBill(bill)}
                      onRecordBill={handleRecordBill}
                      onInstallmentPaid={handleInstallmentPaid}
                      onInstallmentApproval={requestInstallmentApproval}
                      onInstallmentPaidDate={handleInstallmentPaidDate}
                      onPreviewHover={handlePreviewHover}
                      lineHasPreview={lineHasPreview}
                      taskEndDateById={taskEndDateById}
                      hubProjectId={hubProjectIdByDeal.get(summary.dealId) ?? null}
                      onHubHover={(projectId) => {
                        hoverHubProjectIdRef.current = projectId;
                      }}
                      onOpenSuppliers={
                        hubProjectIdByDeal.has(summary.dealId)
                          ? () => navigate(projectHubViewPath(hubProjectIdByDeal.get(summary.dealId)!, 'suppliers'))
                          : null
                      }
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {(quickLook || quickLookLoading) && (
        <div
          className="bp-quicklook-overlay"
          onClick={() => {
            setQuickLook(null);
            setQuickLookLoading(false);
          }}
        >
          {quickLook ? (
            <div className="bp-quicklook" onClick={(e) => e.stopPropagation()}>
              <header className="bp-quicklook-head">
                <span className="bp-quicklook-name" title={quickLook.name}>
                  {quickLook.name}
                </span>
                <span className="bp-quicklook-hint">Space or Esc to close</span>
                <a
                  className="bp-quicklook-open"
                  href={quickLook.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Open in a new tab"
                >
                  <ExternalLink size={14} />
                </a>
                <button
                  type="button"
                  className="bp-quicklook-close"
                  onClick={() => setQuickLook(null)}
                  aria-label="Close preview"
                >
                  <X size={16} />
                </button>
              </header>
              <iframe className="bp-quicklook-frame" src={quickLook.url} title={quickLook.name} />
            </div>
          ) : (
            <Loader2 size={30} className="bp-spin bp-quicklook-spinner" />
          )}
        </div>
      )}

      {approvePrompt && (
        <div className="fin-modal-overlay" onClick={() => setApprovePrompt(null)}>
          <div className="fin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Approve for payment</h3>
              <button type="button" className="fin-modal-close" onClick={() => setApprovePrompt(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <p className="fin-balance-empty" style={{ marginTop: 0 }}>
                {approvalPaymentLabel(approvePrompt.bill, approvePrompt.installmentIndex)}
                . Approving notifies the office team member who pays this bill.
              </p>
              <label className="fin-modal-label" htmlFor="bp-approve-note">
                Message for the person paying this
              </label>
              <textarea
                id="bp-approve-note"
                className="fin-modal-textarea"
                rows={3}
                maxLength={500}
                value={approveNote}
                placeholder="More was added to the order so the QB Bill will need to be updated."
                onChange={(e) => setApproveNote(e.target.value)}
                autoFocus
              />
            </div>
            <div className="fin-modal-footer">
              <div className="fin-modal-footer-right">
                <button type="button" className="fin-secondary-btn" onClick={() => setApprovePrompt(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fin-primary-btn"
                  disabled={busyKey != null}
                  onClick={() => { void confirmInstallmentApproval(); }}
                >
                  {busyKey != null ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {payConfirm && (
        <div className="fin-modal-overlay" onClick={() => setPayConfirm(null)}>
          <div className="fin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Mark paid</h3>
              <button type="button" className="fin-modal-close" onClick={() => setPayConfirm(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <p className="fin-balance-empty" style={{ marginTop: 0 }}>
                {payConfirm.bill.vendorName} — scheduled {fmtCents(payConfirm.suggestion.scheduledAmount)}
              </p>
              {payConfirm.suggestion.earlyPayDiscountPercent && payConfirm.suggestion.earlyPayWithinDays ? (
                <p className="fin-balance-empty">
                  {payConfirm.suggestion.earlyPayApplies
                    ? `${payConfirm.suggestion.earlyPayDiscountPercent}% early-pay discount applies within ${payConfirm.suggestion.earlyPayWithinDays} days. Suggested ${fmtCents(payConfirm.suggestion.suggestedAmount)}.`
                    : payConfirm.suggestion.windowUndated
                      ? `${payConfirm.suggestion.earlyPayDiscountPercent}% within ${payConfirm.suggestion.earlyPayWithinDays} days is available, but the start date is not set. The scheduled amount is shown — you can apply the discount.`
                      : `The ${payConfirm.suggestion.earlyPayWithinDays}-day window for the ${payConfirm.suggestion.earlyPayDiscountPercent}% early-pay discount has passed.`}
                </p>
              ) : null}
              {payConfirm.suggestion.windowUndated && payConfirm.suggestion.discountedAmount != null && (
                <button
                  type="button"
                  className="fin-secondary-btn"
                  style={{ marginBottom: 12 }}
                  onClick={() => setPayAmountInput(payConfirm.suggestion.discountedAmount!.toFixed(2))}
                >
                  Use {payConfirm.suggestion.earlyPayDiscountPercent}% ({fmtCents(payConfirm.suggestion.discountedAmount)})
                </button>
              )}
              <label className="fin-modal-label" htmlFor="bp-pay-amount">
                Amount paid
              </label>
              <input
                id="bp-pay-amount"
                className="fin-modal-input"
                type="text"
                inputMode="decimal"
                value={payAmountInput}
                onChange={(e) => setPayAmountInput(e.target.value)}
                autoFocus
              />
            </div>
            <div className="fin-modal-footer">
              <div className="fin-modal-footer-right">
                <button type="button" className="fin-secondary-btn" onClick={() => setPayConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fin-primary-btn"
                  disabled={parsePaidAmountInput(payAmountInput) == null || busyKey != null}
                  onClick={() => { void confirmPayAmount(); }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {qbModalBill && (
        <div className="fin-modal-overlay" onClick={() => setQbModalBill(null)}>
          <div className="fin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Entered in QuickBooks</h3>
              <button type="button" className="fin-modal-close" onClick={() => setQbModalBill(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <p className="fin-balance-empty" style={{ marginTop: 0 }}>
                {qbModalBill.vendorName} — {fmt(qbModalBill.amount)}
              </p>
              <label className="fin-modal-label" htmlFor="bp-qb-note">
                QuickBooks reference (bill # / memo, optional)
              </label>
              <input
                id="bp-qb-note"
                className="fin-modal-input"
                type="text"
                value={qbNote}
                onChange={(e) => setQbNote(e.target.value)}
                placeholder="e.g. Bill #1042"
              />
            </div>
            <div className="fin-modal-footer">
              <div className="fin-modal-footer-right">
                <button type="button" className="fin-secondary-btn" onClick={() => setQbModalBill(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fin-primary-btn"
                  disabled={busyKey === qbModalBill.id}
                  onClick={async () => {
                    await handleBillStatus(qbModalBill, 'entered_in_qb', qbNote);
                    setQbModalBill(null);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {amountEditBill && (
        <div className="fin-modal-overlay" onClick={() => setAmountEditBill(null)}>
          <div className="fin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Set bill amount</h3>
              <button type="button" className="fin-modal-close" onClick={() => setAmountEditBill(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <p className="fin-balance-empty" style={{ marginTop: 0 }}>
                {amountEditBill.vendorName} — {amountEditBill.description}
              </p>
              <label className="fin-modal-label" htmlFor="bp-amount-edit">
                Amount
              </label>
              <input
                id="bp-amount-edit"
                className="fin-modal-input"
                type="number"
                min={1}
                value={amountEditValue}
                onChange={(e) => setAmountEditValue(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="fin-modal-footer">
              <div className="fin-modal-footer-right">
                <button type="button" className="fin-secondary-btn" onClick={() => setAmountEditBill(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fin-primary-btn"
                  disabled={busyKey === amountEditBill.id}
                  onClick={handleSaveAmount}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {termsEditBill && (
        <SupplierPaymentTermsModal
          open
          vendorName={termsEditBill.vendorName}
          quoteLabel={termsEditBill.description || 'Supplier bill'}
          billAmount={termsEditBill.amount}
          projectId={termsEditBill.projectId}
          billCategory={termsEditBill.category}
          initialTerms={termsEditBill.paymentTerms ?? null}
          lockedInstallmentIds={releasedInstallmentIds(termsEditBill)}
          paidInstallmentIds={paidInstallmentIds(termsEditBill)}
          onSave={async (terms) => {
            if (!termsEditBill.id) return;
            // Editing this bill's own terms in Billing & Payables may raise amount
            // when a later invoice changes the schedule (799e863).
            await setSupplierBillPaymentTerms(termsEditBill.id, terms, {
              expectedRfqSubmissionId: termsEditBill.rfqSubmissionId,
              allowRaiseBillAmount: true,
            });
          }}
          onClose={() => setTermsEditBill(null)}
        />
      )}

      {uploadOpen && (
        <div className="fin-modal-overlay" onClick={() => setUploadOpen(false)}>
          <div className="fin-modal fin-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="fin-modal-header">
              <h3>Upload supplier bill</h3>
              <button type="button" className="fin-modal-close" onClick={() => setUploadOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="fin-modal-body">
              <label className="fin-modal-label" htmlFor="bp-up-vendor">
                Vendor (required)
              </label>
              <input
                id="bp-up-vendor"
                className="fin-modal-input"
                type="text"
                value={upVendor}
                onChange={(e) => setUpVendor(e.target.value)}
                placeholder="e.g. Fastenal"
              />
              <div className="fin-modal-grid">
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="bp-up-amount">
                    Amount (required)
                  </label>
                  <input
                    id="bp-up-amount"
                    className="fin-modal-input"
                    type="number"
                    min={1}
                    value={upAmount}
                    onChange={(e) => setUpAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="bp-up-category">
                    Category
                  </label>
                  <select
                    id="bp-up-category"
                    className="fin-modal-select"
                    value={upCategory}
                    onChange={(e) => setUpCategory(e.target.value as SupplierBillCategory)}
                  >
                    {BILL_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {SUPPLIER_BILL_CATEGORY_LABELS[c]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="fin-modal-label" htmlFor="bp-up-desc">
                Description
              </label>
              <input
                id="bp-up-desc"
                className="fin-modal-input"
                type="text"
                value={upDescription}
                onChange={(e) => setUpDescription(e.target.value)}
                placeholder="What is this bill for?"
              />
              <label className="fin-modal-label">Project (optional)</label>
              <QBDealPicker
                value={upDealId}
                onChange={setUpDealId}
                dealOptions={dealOptions}
                emptyLabel="No project — company expense"
                aria-label="Bill project"
              />
              <div className="fin-modal-grid">
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="bp-up-bucket">
                    P&amp;L bucket (optional)
                  </label>
                  <select
                    id="bp-up-bucket"
                    className="fin-modal-select"
                    value={upBucket}
                    onChange={(e) => setUpBucket(e.target.value as ProjectPLBucket | '')}
                  >
                    <option value="">Auto</option>
                    {PROJECT_PL_BUCKETS.map((b) => (
                      <option key={b} value={b}>
                        {PROJECT_PL_BUCKET_LABELS[b]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fin-modal-field">
                  <label className="fin-modal-label" htmlFor="bp-up-due">
                    Due date (optional)
                  </label>
                  <input
                    id="bp-up-due"
                    className="fin-modal-input"
                    type="date"
                    value={upDueDate}
                    onChange={(e) => setUpDueDate(e.target.value)}
                  />
                </div>
              </div>
              <label className="fin-modal-label" htmlFor="bp-up-file">
                Bill file (PDF / image, optional)
              </label>
              <input
                id="bp-up-file"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setUpFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="fin-modal-footer">
              <div className="fin-modal-footer-right">
                <button type="button" className="fin-secondary-btn" onClick={() => setUploadOpen(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="fin-primary-btn"
                  disabled={uploading}
                  onClick={handleUpload}
                >
                  {uploading ? <Loader2 size={13} className="bp-spin" /> : <Plus size={13} />}
                  Save bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <BillingsHubModal open={showBillingsHub} onClose={() => setShowBillingsHub(false)} />
      <BillPayFlowPanel open={showPayFlow} onClose={() => setShowPayFlow(false)} />
      {partialPicker && (
        <PartialInvoicePackageModal
          open
          installmentId={partialPicker.row.installmentId || 'pre_shipment'}
          milestoneLabel={partialPicker.row.label}
          milestoneDetail={partialPicker.row.detail}
          baseAmount={partialPicker.baseAmount}
          milestoneAmount={partialPicker.milestoneAmount}
          coAdjustments={partialPicker.coAdjustments}
          packages={partialPicker.packages}
          structures={partialPicker.structures}
          invoices={
            salesOrders.find((order) => order.id === partialPicker.row.salesOrderId)?.installmentInvoices || []
          }
          onClose={() => setPartialPicker(null)}
          onConfirm={(selection) => {
            const row = partialPicker.row;
            setPartialPicker(null);
            void finishMarkReady(
              row,
              selection.structureIds.length || Array.isArray(selection.foldChangeOrderRefs) ? selection : null,
            );
          }}
        />
      )}
    </div>
  );
};

export default BillingPayablesPage;
